// Custom Carousel - requires function debounce

class Carousel {
    constructor(selector, options = {}) {
        this.container = document.querySelector(selector);
        if (!this.container) throw new Error(`Element ${selector} not found`);

        this.defaultVisibleItems = options.visibleItems || 2;
        this.visibleItems = this.defaultVisibleItems;
        this.breakpoints = options.breakpoints || {};
        this.disabledBreakpoints = options.disabledBreakpoints || [];
        this.interval = options.interval || 3000;
        this.transitionDuration = options.transitionDuration || 300;
        this.enableArrows = options.enableArrows || false;
        this.enableJumpLinks = options.enableJumpLinks || false;
        this.customLinksSelector = options.customLinksSelector || null;
        this.infinite = (options.infinite !== undefined) ? options.infinite : true;

        // Default values for peek/gap/offset if none provided
        this.peek = options.peek || '0px';
        this.gap = options.gap || '0px';
        this.offset = options.offset || '0px';

        // Responsive layout object for visibleItems, peek, gap, offset
        this.responsiveLayout = options.responsiveLayout || {};

        this.autoSlide = (options.autoSlide !== undefined) ? options.autoSlide : true;

        // Option to enable or disable reduced opacity
        this.reduceOpacity = options.reduceOpacity || true;

        // Hide the first cloned item while preserving space
        this.hideFirstClone = options.hideFirstClone || false;

        // Arrow placement
        this.arrowPlacement = options.arrowPlacement || 'inside';

        this.prevArrow = null;
        this.nextArrow = null;

        this.wrapper = this.container.firstElementChild;
        this.items = Array.from(this.wrapper.children);
        this.totalItems = this.items.length;
        this.currentIndex = 0;
        this.isEnabled = true;
        this.intervalId = null;

        this.isSwiping = false;
        this.startX = 0;
        this.threshold = 0;
        this.itemWidth = 0;
        this.gapPx = 0;
        this.offsetPx = 0;

        this.init();
        window.addEventListener('resize', debounce(() => this.handleResize(), 200));
    }


    init() {
        this.updateEnabledState();

        const { chosenVisibleItems, chosenPeek, chosenGap, chosenOffset } = this.determineResponsiveValues();
        this.visibleItems = chosenVisibleItems;

        this.updateColumnClass();
        this.wrapper.style.setProperty('--peek', this.ensureUnit(chosenPeek));
        this.wrapper.style.setProperty('--gap', this.ensureUnit(chosenGap));

        if (this.isEnabled && this.infinite) {
            this.addClones();
            // Start from index 1, so the second slide is “center”
            this.currentIndex = 1;
        }

        // Always add arrows if enabled
        if (this.enableArrows) {
            this.addArrows(); 
        }

        // Always create jump links if enabled, regardless of enabled state
        if (this.enableJumpLinks) {
            this.createJumpLinks();
        }

        // If customLinksSelector provided, attach them
        if (this.customLinksSelector) {
            this.attachCustomLinks();
        }

        // Add interaction events once
        this.addInteractionEvents();

        requestAnimationFrame(() => {
            this.itemWidth = this.items[0].getBoundingClientRect().width;
            this.gapPx = this.convertToPx(chosenGap);
            this.offsetPx = this.convertToPx(chosenOffset);
            this.threshold = this.itemWidth * 0.2;

            if (this.enableArrows) {
                this.updateArrowVisibility();
            }

            if (this.isEnabled) {
                if (this.autoSlide && this.shouldSlide()) {
                    this.startAutoSlide();
                }

                this.preloadImages && this.preloadImages();
            }

            const stepSize = this.itemWidth + this.gapPx;
            const initialTransformValue = this.offsetPx - (this.currentIndex * stepSize);
            this.wrapper.style.transform = `translateX(${initialTransformValue}px)`;


            this.updateItemOpacity && this.updateItemOpacity(); // Update Opacity
        });
    }

    handleResize() {
        // Re-check enabled state on every resize
        this.updateEnabledState();

        const { chosenVisibleItems, chosenPeek, chosenGap, chosenOffset } = this.determineResponsiveValues();
        this.visibleItems = chosenVisibleItems;

        this.wrapper.style.setProperty('--peek', this.ensureUnit(chosenPeek));
        this.wrapper.style.setProperty('--gap', this.ensureUnit(chosenGap));
        this.updateColumnClass();

        requestAnimationFrame(() => {
            this.itemWidth = this.items[0].getBoundingClientRect().width;
            this.gapPx = this.convertToPx(chosenGap);
            this.offsetPx = this.convertToPx(chosenOffset);
            this.threshold = this.itemWidth * 0.2;

            if (this.enableArrows) this.updateArrowVisibility(); // Update arrow visibility on resize

            const stepSize = this.itemWidth + this.gapPx;
            const transformValue = this.offsetPx - (this.currentIndex * stepSize);
            this.wrapper.style.transform = `translateX(${transformValue}px)`; // Update transform position

            this.updateItemOpacity && this.updateItemOpacity(); // Update opacity after resize
        });
    }



    determineResponsiveValues() {
        const layoutKeys = Object.keys(this.responsiveLayout || {});
        if (layoutKeys.length === 0) {
            // No responsive layout, return defaults
            return {
                chosenVisibleItems: this.defaultVisibleItems,
                chosenPeek: this.peek,
                chosenGap: this.gap,
                chosenOffset: this.offset
            };
        }

        const breakpoints = layoutKeys.map(k => parseInt(k,10)).sort((a,b) => b - a);
        const viewportWidth = window.innerWidth;

        let chosenVisibleItems = this.defaultVisibleItems;
        let chosenPeek = this.peek;
        let chosenGap = this.gap;
        let chosenOffset = this.offset;

        for (let bp of breakpoints) {
            if (viewportWidth <= bp) {
                const layout = this.responsiveLayout[bp];
                if (layout.visibleItems !== undefined) chosenVisibleItems = layout.visibleItems;
                if (layout.peek !== undefined) chosenPeek = layout.peek;
                if (layout.gap !== undefined) chosenGap = layout.gap;
                if (layout.offset !== undefined) chosenOffset = layout.offset;
            }
        }

        return { chosenVisibleItems, chosenPeek, chosenGap, chosenOffset };
    }

    updateEnabledState() {
        const wasEnabled = this.isEnabled;
        const viewportWidth = window.innerWidth;
        const nowEnabled = !this.disabledBreakpoints.some(([min, max]) => {
            return viewportWidth >= min && viewportWidth <= max;
        });

        // If state hasn't changed, do nothing
        if (wasEnabled === nowEnabled) {
            return; // No state change, no need to enable/disable again
        }

        this.isEnabled = nowEnabled;

        if (!this.isEnabled) {
            // Transitioning from enabled to disabled
            this.container.classList.add('carousel-disabled');
            this.disableCarousel();
        } else {
            // Transitioning from disabled to enabled
            this.container.classList.remove('carousel-disabled');
            this.enableCarousel();
        }
    }


    enableCarousel() {
        // //console.log("enable");
        if (this.isEnabled) return;
        this.isEnabled = true;
        this.wrapper.classList.remove('columns-default');
        this.setupCarousel();
    }

    disableCarousel() {
        // //console.log("disable");
        // Removed the if check
        this.isEnabled = false;
        // //console.log("being disable");

        // Remove transition to avoid any animations
        this.wrapper.style.transition = 'none';

        // Reset position if needed
        this.currentIndex = 0;
        this.wrapper.style.transform = '';

        this.wrapper.classList.remove('columns-1','columns-2','columns-3','columns-4','columns-5');
        this.wrapper.classList.add('columns-default');

        const clones = this.wrapper.querySelectorAll('[aria-hidden="true"]');
        clones.forEach(clone => clone.remove());

        this.stopAutoSlide();

        const controls = this.container.querySelectorAll('.carousel-arrow');
        controls.forEach(control => (control.style.display = 'none'));
    }



    setupCarousel() {
        this.updateVisibleItems();
        this.updateColumnClass();
        this.addClones();

        const { chosenVisibleItems, chosenPeek, chosenGap, chosenOffset } = this.determineResponsiveValues();
        this.visibleItems = chosenVisibleItems;
        this.wrapper.style.setProperty('--peek', this.ensureUnit(chosenPeek));
        this.wrapper.style.setProperty('--gap', this.ensureUnit(chosenGap));

        requestAnimationFrame(() => {
            this.itemWidth = this.items[0].getBoundingClientRect().width;
            this.gapPx = this.convertToPx(chosenGap);
            this.offsetPx = this.convertToPx(chosenOffset);
            this.threshold = this.itemWidth * 0.2;

            if (this.enableArrows) {
                this.updateArrowVisibility();
            }

            if (this.autoSlide && this.shouldSlide()) this.startAutoSlide();
            this.preloadImages && this.preloadImages();
        });
    }

    addClones() {
        const existingClones = this.wrapper.querySelectorAll('[aria-hidden="true"]');
        existingClones.forEach(clone => clone.remove()); // Remove old clones

        if (!this.infinite) return; // No clones if not infinite

        // 1) Clone the last item and prepend it
        const lastItemClone = this.items[this.totalItems - 1].cloneNode(true);
        lastItemClone.setAttribute('aria-hidden', 'true');
        this.wrapper.insertBefore(lastItemClone, this.wrapper.firstChild);

        // 2) Conditionally add .my-cloned-item if hideFirstClone is true
        if (this.hideFirstClone) {
            lastItemClone.classList.add('my-cloned-item');
        }

        // 3) Existing logic to clone visibleItems + 2 items at the end
        for (let i = 0; i < this.visibleItems + 2; i++) {
            const originalIndex = i % this.totalItems;
            const clone = this.items[originalIndex].cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            this.wrapper.appendChild(clone);
        }

        // Reapply opacity to ensure consistency
        this.updateItemOpacity && this.updateItemOpacity();
    }




    updateVisibleItems() {
        const viewportWidth = window.innerWidth;
        let matchedVisibleItems = this.defaultVisibleItems;

        const sortedBreakpoints = Object.entries(this.breakpoints).sort((a,b) => b[0]-a[0]);
        for (const [breakpoint, items] of sortedBreakpoints) {
            if (viewportWidth <= breakpoint) matchedVisibleItems = items;
        }

        this.visibleItems = matchedVisibleItems;
    }

    updateColumnClass() {
        this.wrapper.classList.remove('columns-1','columns-2','columns-3','columns-4','columns-5','columns-default');
        this.wrapper.classList.add(`columns-${this.visibleItems}`);
    }

    shouldSlide() {
        return this.totalItems > this.visibleItems;
    }

    addArrows() {
        // If we've already created arrows, do nothing
        if (this.prevArrow && this.nextArrow) return;

        // 1) Create a wrapper div for styling purposes
        const arrowWrapper = document.createElement('div');
        arrowWrapper.classList.add('carousel-arrows-wrapper');
        // You can style this class in your CSS however you like

        // 2) Create the actual Prev and Next arrow buttons
        const prevArrow = document.createElement('button');
        prevArrow.classList.add('carousel-arrow', 'carousel-arrow-prev');

        const nextArrow = document.createElement('button');
        nextArrow.classList.add('carousel-arrow', 'carousel-arrow-next');

        // 3) Store them on the instance
        this.prevArrow = prevArrow;
        this.nextArrow = nextArrow;

        // 4) Append the arrows to the wrapper
        arrowWrapper.appendChild(prevArrow);
        arrowWrapper.appendChild(nextArrow);

        // 5) Decide where to place the wrapper based on user option
        switch (this.arrowPlacement) {
            case 'before': {
                const parentNode = this.container.parentNode;
                if (!parentNode) return;

                parentNode.insertBefore(arrowWrapper, this.container);
                break;
            }
            case 'after': {
                const parentNode = this.container.parentNode;
                if (!parentNode) return;

                const nextSibling = this.container.nextSibling;
                if (nextSibling) {
                    parentNode.insertBefore(arrowWrapper, nextSibling);
                } else {
                    parentNode.appendChild(arrowWrapper);
                }
                break;
            }
            case 'inside':
            default: {
                this.container.appendChild(arrowWrapper);
                break;
            }
        }

        // 6) Add click events
        prevArrow.addEventListener('click', () => {
            if (this.stopAutoSlide) this.stopAutoSlide();
            if (this.prevSlide) this.prevSlide();
        });

        nextArrow.addEventListener('click', () => {
            if (this.stopAutoSlide) this.stopAutoSlide();
            if (this.nextSlide) this.nextSlide();
        });
    }



    updateArrowVisibility() {
        // If the arrows haven't been created yet, do nothing
        if (!this.prevArrow || !this.nextArrow) return;

        // 1) Hide or show arrows based on whether we can slide at all
        if (!this.shouldSlide()) {
            this.prevArrow.style.display = 'none';
            this.nextArrow.style.display = 'none';
            return;
        } else {
            this.prevArrow.style.display = 'flex';
            this.nextArrow.style.display = 'flex';
        }

        // 2) If we're NOT in infinite mode, disable arrows at the edges
        if (!this.infinite) {
            // Disable "prev" arrow if we're at the first item
            if (this.currentIndex <= 0) {
                this.prevArrow.setAttribute('disabled', 'disabled');
            } else {
                this.prevArrow.removeAttribute('disabled');
            }

            // Disable "next" arrow if we're at (or beyond) the last item
            if (this.currentIndex >= this.totalItems - 1) {
                this.nextArrow.setAttribute('disabled', 'disabled');
            } else {
                this.nextArrow.removeAttribute('disabled');
            }

            return; // Done for non-infinite mode
        }

        // 3) Infinite mode: re-enable both by default
        this.prevArrow.removeAttribute('disabled');
        this.nextArrow.removeAttribute('disabled');

        // 4) If hideFirstClone is on AND the clone is still present, disable prev at index=1
        if (this.hideFirstClone && this.currentIndex === 1) {
            const firstClonedItem = this.wrapper.querySelector('.my-cloned-item');
            if (firstClonedItem) {
                this.prevArrow.setAttribute('disabled', 'disabled');
            }
        }
    }






    startAutoSlide() {
        this.stopAutoSlide();
        this.intervalId = setInterval(() => this.nextSlide(), this.interval);
    }

    stopAutoSlide() {
        if (this.intervalId) clearInterval(this.intervalId);
        this.intervalId = null;
    }

    preloadImages() {
        const indexesToPreload = [
            this.currentIndex,
            (this.currentIndex + 1) % this.totalItems,
            (this.currentIndex - 1 + this.totalItems) % this.totalItems
        ];

        indexesToPreload.forEach((index) => {
            const item = this.wrapper.children[index];
            if (item) {
                const pictures = item.querySelectorAll('picture');
                pictures.forEach((picture) => {
                    const sources = picture.querySelectorAll('source[data-srcset]');
                    sources.forEach((source) => {
                        if (!source.srcset) {
                            source.srcset = source.getAttribute('data-srcset');
                            source.removeAttribute('data-srcset');
                        }
                    });

                    const img = picture.querySelector('img.lazy:not(.loaded)');
                    if (img && img.getAttribute('data-src')) {
                        img.src = img.getAttribute('data-src');
                        img.classList.add('loaded');
                        img.removeAttribute('data-src');
                    }
                });
            }
        });
    }

    goToSlide(index) {
        if (!this.isEnabled) return;
        this.stopAutoSlide();

        // Shift by +1 so item #1 is physically "first" in your offset logic
        let realIndex = (index + 1) % this.totalItems;

        // Force the last jump link to map to the physical last item
        if (index === this.totalItems - 1) {
            realIndex = this.totalItems;
        }

        this.currentIndex = realIndex;

        //update arrow visibility
        this.updateArrowVisibility && this.updateArrowVisibility();

        // Existing transform logic
        const stepSize = this.itemWidth + this.gapPx;
        const transformValue = this.offsetPx - (this.currentIndex * stepSize);
        this.wrapper.style.transition = `transform ${this.transitionDuration}ms ease-in-out`;
        this.wrapper.style.transform = `translateX(${transformValue}px)`;

        // The rest is unchanged...
        this.preloadImages && this.preloadImages();
        this.updateActiveJumpLink && this.updateActiveJumpLink();
        this.updateItemOpacity && this.updateItemOpacity();
    }




    nextSlide() {
        // 1) If already in the middle of a transition, ignore further clicks/swipes
        if (this.isAnimating) return;
        this.isAnimating = true;

        // 2) Standard logic
        this.currentIndex++;
        const stepSize = this.itemWidth + this.gapPx;
        this.wrapper.style.transition = `transform ${this.transitionDuration}ms ease-in-out`;
        const transformValue = this.offsetPx - (this.currentIndex * stepSize);
        this.wrapper.style.transform = `translateX(${transformValue}px)`;

        this.updateArrowVisibility && this.updateArrowVisibility();

        // 3) If we need to wrap from the 'fake' last slide back to index=1
        if (this.infinite && this.currentIndex >= (this.totalItems + 1)) {
            setTimeout(() => {
                this.wrapper.style.transition = 'none';
                this.currentIndex = 1;
                const resetTransform = this.offsetPx - (this.currentIndex * stepSize);
                this.wrapper.style.transform = `translateX(${resetTransform}px)`;

                // Possibly remove the .my-cloned-item if needed
                const firstClonedItem = this.wrapper.querySelector('.my-cloned-item');
                if (firstClonedItem) {
                    firstClonedItem.classList.remove('my-cloned-item');
                    this.updateArrowVisibility && this.updateArrowVisibility();
                }

                // Force reflow by accessing an element property (e.g., offsetHeight)
                this.wrapper.offsetHeight; 

                // Re-enable transitions
                requestAnimationFrame(() => {
                    this.wrapper.style.transition = 
                        `transform ${this.transitionDuration}ms ease-in-out`;
                });

                this.updateActiveJumpLink && this.updateActiveJumpLink();
                this.updateItemOpacity && this.updateItemOpacity();

                // 4) Done, now allow another click
                this.isAnimating = false;

            }, this.transitionDuration);
        } else {
            // Normal path
            this.updateActiveJumpLink && this.updateActiveJumpLink();
            this.updateItemOpacity && this.updateItemOpacity();
            this.preloadImages && this.preloadImages();

            // 4) If no wrap needed, just wait for the transition to finish
            setTimeout(() => {
                this.isAnimating = false;
            }, this.transitionDuration);
        }
    }





    prevSlide() {
        // 1) No slide if there aren't enough items
        if (!this.shouldSlide()) return;

        const stepSize = this.itemWidth + this.gapPx;

        // 2) Decrement currentIndex
        this.currentIndex--;

        //update arrowvisibility
        this.updateArrowVisibility && this.updateArrowVisibility();

        // 3) TWO-STEP LOGIC WHEN AT index=0/1
        // -------------------------------------------------------
        // If infinite is ON and we land at index=0 or index < 0,
        // we want to do a *fake* backward wrap so the user sees an animation.
        if (this.infinite && this.currentIndex <= 0) {
            // Step A: Jump instantly to the "fake end" (index = this.totalItems + 1)
            this.wrapper.style.transition = 'none';
            this.currentIndex = this.totalItems + 1;

            const jumpTransform = this.offsetPx - (this.currentIndex * stepSize);
            this.wrapper.style.transform = `translateX(${jumpTransform}px)`;

            // Re-enable transition on next frame, then animate backward to last item
            requestAnimationFrame(() => {
                // Step B: Animate from the fake end down to the last real item
                this.wrapper.style.transition = `transform ${this.transitionDuration}ms ease-in-out`;
                this.currentIndex = this.totalItems; // The last real item
                const animTransform = this.offsetPx - (this.currentIndex * stepSize);
                this.wrapper.style.transform = `translateX(${animTransform}px)`;

                // Update after the animation target is set
                this.updateActiveJumpLink && this.updateActiveJumpLink();
                this.updateItemOpacity && this.updateItemOpacity();
                this.preloadImages && this.preloadImages();
            });

            return; // IMPORTANT: Stop here so no other logic runs
        }
        // -------------------------------------------------------

        // 4) Normal backward sliding (if not infinite or currentIndex > 0)
        const transformValue = this.offsetPx - (this.currentIndex * stepSize);
        this.wrapper.style.transition = `transform ${this.transitionDuration}ms ease-in-out`;
        this.wrapper.style.transform = `translateX(${transformValue}px)`;

        // 5) Updates
        this.updateActiveJumpLink && this.updateActiveJumpLink();
        this.updateItemOpacity && this.updateItemOpacity();
        this.preloadImages && this.preloadImages();
    }





    createJumpLinks() {
        let jumpLinksContainer = this.container.querySelector('.carousel-jump-links');
        if (jumpLinksContainer) jumpLinksContainer.remove();

        jumpLinksContainer = document.createElement('div');
        jumpLinksContainer.classList.add('carousel-jump-links');

        this.jumpLinks = [];

        for (let i = 0; i < this.totalItems; i++) {
            const jumpLink = document.createElement('button');
            jumpLink.classList.add('carousel-jump-link');
            jumpLink.textContent = `Slide ${i + 1}`;
            jumpLink.addEventListener('click', () => this.goToSlide(i));
            jumpLinksContainer.appendChild(jumpLink);

            this.jumpLinks.push(jumpLink);
        }

        this.container.appendChild(jumpLinksContainer);

        this.updateActiveJumpLink && this.updateActiveJumpLink();
    }

    attachCustomLinks() {
        const customLinks = document.querySelectorAll(this.customLinksSelector);
        customLinks.forEach(link => {
            const slideIndex = parseInt(link.getAttribute('data-slide'), 10);
            if (!isNaN(slideIndex)) {
                link.addEventListener('click', () => this.goToSlide(slideIndex));
            }
            if (!this.jumpLinks) this.jumpLinks = [];
            this.jumpLinks.push(link);
        });

        this.updateActiveJumpLink && this.updateActiveJumpLink();
    }


    updateActiveJumpLink() {
        if (!this.jumpLinks) return;

        // Offset by 1 so that when currentIndex=1, linkIndex=0
        let linkIndex = (this.currentIndex - 1 + this.totalItems) % this.totalItems;


        // If we've gone one beyond totalItems+1, force the first link as active (index 0)
        if (this.currentIndex >= this.totalItems + 1) {
            linkIndex = 0;
        }

        this.jumpLinks.forEach((link, i) => {
            if (i === linkIndex) {
                link.classList.add('active');
            } else {
                link.classList.remove('active');
            }
        });
    }


    addInteractionEvents() {
        // Create a Hammer instance for the wrapper element
        const hammer = new Hammer(this.wrapper);

        // Configure the pan gesture for dragging/swiping
        hammer.get('pan').set({ direction: Hammer.DIRECTION_HORIZONTAL });

        // Debugging for Hammer instance setup
        //console.log('Hammer.js instance created for:', this.wrapper);

        // Handle pan (drag/swipe) events
        hammer.on('pan', (e) => {
            // console.log('Pan detected:', {
            //     deltaX: e.deltaX,
            //     direction: e.direction,
            //     distance: e.distance,
            //     velocityX: e.velocityX
            // });

            // Stop auto-slide when user interacts
            this.stopAutoSlide();

            const stepSize = this.itemWidth + this.gapPx;
            const transformValue = this.offsetPx - this.currentIndex * stepSize + e.deltaX;
            this.wrapper.style.transform = `translateX(${transformValue}px)`;
        });

        // Handle pan end (when dragging stops)
        hammer.on('panend', (e) => {
            // console.log('Panend detected:', {
            //     deltaX: e.deltaX,
            //     direction: e.direction,
            //     distance: e.distance,
            //     velocityX: e.velocityX
            // });

            const stepSize = this.itemWidth + this.gapPx;

            if (e.deltaX < -this.threshold) {
                this.nextSlide(); // Swipe left
            } else if (e.deltaX > this.threshold) {
                this.prevSlide(); // Swipe right
            } else {
                // Reset to current position if threshold not met
                const transformValue = this.offsetPx - (this.currentIndex * stepSize);
                this.wrapper.style.transition = `transform ${this.transitionDuration}ms ease-in-out`;
                this.wrapper.style.transform = `translateX(${transformValue}px)`;
            }
        });

        // Optional: Handle tap for any additional features
        hammer.on('tap', () => {
            //console.log('Tapped on the slider');
        });
    }

    updateItemOpacity() {
        if (!this.reduceOpacity) return; // Exit if reduceOpacity is disabled

        // Combine original items and clones
        const allItems = Array.from(this.wrapper.children);

        allItems.forEach((item, index) => {
            // Determine if the item is within the visible range
            const isActive = index >= this.currentIndex && index < this.currentIndex + this.visibleItems;

            // Apply opacity based on active state
            item.style.opacity = isActive ? '1' : '0.5';
        });
    }

    ensureUnit(value) {
        if (value === '0' || value.trim() === '0') return '0px';
        return value;
    }

    convertToPx(valueStr) {
        if (!valueStr || valueStr === '0px') return 0;
        const value = parseFloat(valueStr);
        const unit = valueStr.replace(value, '').trim();

        if (unit === 'px' || unit === '') {
            return value; 
        } else if (unit === 'vw') {
            return (window.innerWidth * value) / 100;
        } else if (unit === '%') {
            return (this.container.clientWidth * value) / 100;
        }
        return value; 
    }
}

// Services
// document.addEventListener('DOMContentLoaded', () => {
//     new Carousel('.latest-offers', {
//         interval: 5000,
//         transitionDuration: 300,
//         enableArrows: true,
//         enableJumpLinks: true,
//         infinite: true,
//         // customLinksSelector: '.service-jump-link',
//         // disabledBreakpoints: [
//         //     [1400, Infinity]
//         // ],
//         visibleItems: 3,
//         gap: '40px',
//         peek: '30vw',
//         offset: '15vw',
//         autoSlide: false,
//         infinite: false,
//         responsiveLayout: {
//             1440: { 
//                 visibleItems: 3,
//                 peek: '20vw', 
//                 gap: '40px', 
//                 offset: '10vw' 
//             },
//             1024: { 
//                 visibleItems: 2,
//                 peek: '20vw',  
//                 gap: '20px', 
//                 offset: '10vw' 
//             },
//             768:  { 
//                 visibleItems: 1,
//                 peek: '10vw',  
//                 gap: '20px',  
//                 offset: '5vw' 
//             }
//         }
//     });
// });

// Reviews
document.addEventListener('DOMContentLoaded', () => {
    const latestOffers = document.querySelector('.latest-offers');
    if (latestOffers) {
        new Carousel('.latest-offers', {
            interval: 8000,
            transitionDuration: 300,
            visibleItems: 3,
            reduceOpacity: true,
            gap: '24px',
            peek: '30vw',
            offset: '15vw',
            hideFirstClone: true,
            responsiveLayout: {
                1919: { 
                    peek: '18vw',  
                    offset: '9vw' 
                },
                1600: { 
                    peek: '6vw',  
                    offset: '3vw' 
                },
                1279: { 
                    visibleItems: 2,
                    peek: '6vw',  
                    offset: '3vw' 
                },
                1024: {
                    peek: '9vw',  
                    offset: '4.5vw' 
                },
                640:  { 
                    visibleItems: 1,
                },
                480:  { 
                    peek: '12vw',   
                    offset: '6vw' 
                }
            },
            enableArrows: true,
            arrowPlacement: 'before', // or 'after' or 'inside'
            enableJumpLinks: true,
        });
    }
});



// Usage example:
// document.addEventListener('DOMContentLoaded', () => {
//     new Carousel('.reviews-block-aside', {
//         interval: 5000,
//         transitionDuration: 400,
//         visibleItems: 3,
//         infinite: true,
//         hideFirstClone: true,
//         disabledBreakpoints: [
//             [1400, Infinity]
//         ],
//         enableArrows: true,
//         arrowPlacement: 'before' // or 'after' or 'inside'
//         enableJumpLinks: true,
//         customLinksSelector: '.jump-link',
//         peek: '5vw',
//         gap: '20px',
//         offset: '5vw', // offset for initial positioning
//         autoSlide: true,
//         responsiveLayout: {
//             1400: { 
//                 visibleItems: 3,
//                 peek: '10vw', 
//                 gap: '20px', 
//                 offset: '5vw' 
//             },
//             1024: { 
//                 visibleItems: 2,
//                 peek: '5vw',  
//                 gap: '10px', 
//                 offset: '2vw' 
//             },
//             768:  { 
//                 visibleItems: 1,
//                 peek: '0px',  
//                 gap: '0px',  
//                 offset: '0px' 
//             }
//         }
//     });
// });
/*jshint unused:false*/
/*global fetch */
/*global console */



// $(function () {

// /**** NAV SCROLL ****/
//     $(".side-nav a, .header-intro a, .footer-content a").click(function(e){
//         e.preventDefault();
//         // Hide nav
//         // Scroll to location
//         $("html, body").animate({ 
//             scrollTop: $( $(this).attr('href') ).position().top+50
//         }, 600);
//     });

// });

//object fit fix for ie11
var userAgent, ieReg, ie;
userAgent = window.navigator.userAgent;
ieReg = /msie|Trident.*rv[ :]*11\./gi;
ie = ieReg.test(userAgent);


if(ie) {
  var imgContainer = document.querySelectorAll(".img-container");

  for(var i=0; i<imgContainer.length; i++){
    var imgCtr = imgContainer[i];
    var imgUrl = imgCtr.getElementsByTagName("img")[0].src;
    if (imgUrl) {
      imgCtr.setAttribute("style", "background-image:url('" + imgUrl + "')");
      imgCtr.classList.add("custom-object-fit");
    }
  }
}


let typeQueryString = document.body.dataset.type;
let priceContainers = [].slice.call(document.querySelectorAll('.deal-price-toggle'));
let priceSwitchForm = document.getElementById('price-switch');
let selected;

document.body.addEventListener('change', (e) => {
  if (e.target.matches('.price-toggle-checkbox') || e.target.closest('.price-toggle')) {
    if (e.target.classList.contains('toggle-personal')) {
      selected = 0;
    } else if (e.target.classList.contains('toggle-salsac')) {
      selected = 2;
    } else if (e.target.classList.contains('toggle-business')) {
      selected = 1;
    }

    if (e.target.classList.contains('price-toggle-checkbox')) {
      selected = e.target.checked ? 1 : 0;
    }

    // Close the nav modal if it's open
    if (modalMobileNav.isOpen()) {
      modalMobileNav.close();
      // Reset the hamburger state
      hamburger.classList.remove('active', 'is-active');
    }

    toggleSwitches();
    togglePrices();
    updatePrice();
  }
});



let toggleSwitches = () => {
  document.querySelectorAll('.price-toggle').forEach((v) => {
  	if (selected != 2) {
	    if (selected === 0) {
	      v.querySelectorAll('.toggle-personal').forEach((toggle) => {
	        toggle.checked = true;
	        toggle.dispatchEvent(new Event('change'));
	      });
	    } else {
	      v.querySelectorAll('.toggle-business').forEach((toggle) => {
	        toggle.checked = true;
	        toggle.dispatchEvent(new Event('change'));
	      });
	    }

	    document.querySelectorAll('.price-toggle-checkbox').forEach((checkbox) => {
	      checkbox.checked = selected === 1;
	    });
	}
  });

  const switchPriceType = document.querySelector("#switch-price-type");
  if (switchPriceType) {
    switchPriceType.value = selected === 0 ? 'Personal' : 'Business';
  }

  document.querySelectorAll(".price-title-type").forEach((v) => {
    v.innerHTML = selected === 0 ? 'Personal' : 'Business';
  });
};

let togglePrices = () => {
  priceContainers.forEach((v) => {
    if (selected === 0) {
      v.querySelector('.price-personal').classList.add('active');
      v.querySelector('.price-business').classList.remove('active');
    } else if (selected === 2) {
      v.querySelector('.price-business').classList.remove('active');
      v.querySelector('.price-personal').classList.remove('active');
    } else {
      v.querySelector('.price-business').classList.add('active');
      v.querySelector('.price-personal').classList.remove('active');
    }
  });
};

let updatePrice = () => {
  if (priceSwitchForm) {
    let url = priceSwitchForm.getAttribute('action');
    let formData = new FormData(priceSwitchForm);
    fetch(url, {
      method: 'POST',
      processData: false,
      contentType: false,
      body: formData,
      headers: {
        'X-CSRF-TOKEN': document.querySelector("#price-switch [name=_token]").value,
        'X-Requested-With': 'XMLHttpRequest'
      },
    })
      .then((response) => {
        if (response.status !== 200) {
          console.log('Looks like there was a problem. Status Code: ' + response.status);
          return;
        }
        response.json().then((data) => {
          document.querySelector("#price-switch [name=_token]").value = data.token;
        });
      })
      .catch((err) => {
        console.log('Fetch Error :-S', err);
      });
  }
};

if (typeQueryString.toLowerCase().includes('personal') || typeQueryString.toLowerCase().includes('business')) {
  selected = typeQueryString.toLowerCase().includes('personal') ? 0 : 1;
  toggleSwitches();
  togglePrices();
  updatePrice();
}



// Price switch 
// function priceswitch(event) {
//   event.preventDefault();
//   document.getElementById('switch-price-type').value = this.value;
//   document.getElementById('price-switch').submit();
// }
// var price_buttons = document.querySelectorAll("#price-switch button");
// for(var i=0; i<price_buttons.length; i++){
//   price_buttons[i].addEventListener('click', priceswitch, false);
// }


//Nav Toggle
function toggleNav(el) {
  var element = el;
  var menu = document.getElementById("site-menu");
  var header = document.querySelectorAll(".site-header")[0];
  menu.classList.toggle("active");
  header.classList.toggle("active");

  if(element.querySelector("span").classList.contains("more")){
    element.innerHTML = "<span class='close'></span> Close";
  } else {
    element.innerHTML = "<span class='more'></span> Menu";
  }
}

//Accordion active
var showAccordion = function(){
  var dropdown = this.parentNode;
  dropdown.classList.toggle("active");
}

var accordionButton = document.querySelectorAll(".accordion > button");

for(var i=0; i<accordionButton.length; i++){
  accordionButton[i].addEventListener('click', showAccordion, false);
}

// // Mobile Dropdown
// var mobileDropdown = function(){
//     var dropdown = this.parentNode;
//     dropdown.classList.toggle("active");
// }

// var mobile_dropdown = document.querySelectorAll(".breadcrumb-menu");

// for(var i=0; i<mobile_dropdown.length; i++){
//   mobile_dropdown[i].addEventListener('click', mobileDropdown, false);
// }

/* MOBILE MODALS */
const navContainer = document.querySelector('.nav-container');
const hamburger = document.querySelector('#hamburger');
const hamburgerSearch = document.querySelector('#headerSearch');

const modalMobileNav = new tingle.modal({
  footer: false,
  closeMethods: ['escape'],
  cssClass: ['mobile-nav','reversed'],
  onOpen: function () {
    // Navigation height
    let headerHeight = 0;
    if (document.querySelector('.site-header').classList.contains('is-sticky')) {
      headerHeight = document.querySelector('.site-header').offsetHeight;
    } else {
      headerHeight = document.querySelector('.site-header').offsetHeight + (document.body.getBoundingClientRect().top + window.scrollY);
    }
    document.querySelector('.mobile-nav.tingle-modal').setAttribute('style', `margin-top:${headerHeight}px;height:calc(100vh - ${headerHeight}px)!important`);
    // document.querySelector('.site-menu') ? modalMobileNav.setFooterContent(document.querySelector('#price-switch').outerHTML) : null;
    // priceToggles();
  }
});
document.querySelector('.site-menu') ? modalMobileNav.setContent(document.querySelector('.site-menu').innerHTML) : null;

// Nav modal
const handleToggle = () => {
  hamburger.classList.toggle('active');
  hamburger.classList.toggle('is-active');
  navContainer.classList.toggle('is-active');
  // hamburger.parentElement.classList.toggle('active');

  if(hamburger.classList.contains("active")) {
    // modalMobileSearch.close();
    modalMobileNav.open();
  } else{
    modalMobileNav.close();
  }
}

hamburger.onclick = () => handleToggle();
hamburger.addEventListener('keyup', (event) => {
  if(event.keyCode === 13 || event.keyCode === 32) {
    handleToggle();
  }
});
/* END MOBILE MODALS */

// Search Bar
var searchDropdown = function(){
  var searchRow = this.closest('.search-bar');
  searchRow.classList.toggle("active");
}

var searchBar = document.querySelectorAll(".search-bar .legend");

for(var i=0; i<searchBar.length; i++){
  searchBar[i].addEventListener('click', searchDropdown, false);
}


if(document.getElementsByClassName('toggle-search').length>0){
  document.getElementsByClassName('toggle-search')[0].addEventListener('click', function(e){
    e.preventDefault();
    if(document.querySelector('#popout-form')===null){
      window.location.href='/search?show-search-bar=true&advanced=true';
      return false;
    }
    if (window.innerWidth <= 959) {
      document.querySelector('#popout-form').classList.toggle('hidden-m');
      document.querySelector('#popout-form').classList.remove('hidden');
    } else {
      if(document.querySelector('#popout-form').classList.contains('hidden')){
        document.querySelector('#popout-form').classList.remove('hidden');
      } else {
        document.querySelector('#popout-form').classList.add('hidden');
      }
    }
  });
}


if(document.getElementsByClassName('showAdvanced').length>0){
  document.getElementsByClassName('showAdvanced')[0].addEventListener('click', function(e){
    e.preventDefault();
    document.querySelector('#popout-form').classList.toggle('advanced');
  });
}

// Back to top button
if(document.getElementById('gotosearch')!==null){
  const scrollToTopButton = document.getElementById('gotosearch');
  let smoothScrollCapture = (x) => {
    event.preventDefault();
    let id = x.currentTarget.target.getAttribute('href').split("#")[1]
    let el = document.getElementById(id);
    var x = el.getBoundingClientRect().left;
    var y = el.getBoundingClientRect().top;
    smoothScrollFunc(x, y)
  }
  let smoothScrollFunc = (x, y) => {
    event.preventDefault();
    window.scrollTo({
      top: y,
      left: x,
      behavior: 'smooth'
    });
  }
  window.addEventListener('DOMContentLoaded', () => {
    scrollToTopButton.addEventListener('click', () => {
      smoothScrollFunc(0, 0);
    })
  })
  window.addEventListener('scroll', () => {
    let pageHeight = document.body.scrollHeight;
    let currentHeight = window.scrollY;
    (currentHeight > (pageHeight/2)) ? scrollToTopButton.classList.add('active') : scrollToTopButton.classList.remove('active');
  })
}
// End back to top button



//Menu Dropdown Toggle
// var menuDropdown = function(){
//     var dropdown = this;
//     dropdown.classList.toggle("active");
// }

// var menuTrigger = document.querySelectorAll(".menu-dropdown");

// for(var i=0; i<menuTrigger.length; i++){
//   menuTrigger[i].addEventListener('click', menuDropdown, false);
// }

// //Dropdown Button
// var showDropdown = function(){
//     var dropdown = this.parentNode;
//     dropdown.classList.toggle("active");
// }

// var dropdownButton = document.querySelectorAll(".dropdown > button");

// for(var i=0; i<dropdownButton.length; i++){
//   dropdownButton[i].addEventListener('click', showDropdown, false);
// }

// //Dropdown Toggle
// var showDropdown = function(){
//     var dropdown = this.querySelector('div');
//     dropdown.classList.toggle("active");
// }

// var has_dropdown = document.querySelectorAll(".has-dropdown");

// for(var i=0; i<has_dropdown.length; i++){
//   has_dropdown[i].addEventListener('click', showDropdown, false);
// }

// // Mobile Dropdown
// var mobileDropdown = function(){
//     var dropdown = this.parentNode;
//     dropdown.classList.toggle("active");
// }

// var mobile_dropdown = document.querySelectorAll(".breadcrumb-menu");

// for(var i=0; i<mobile_dropdown.length; i++){
//   mobile_dropdown[i].addEventListener('click', mobileDropdown, false);
// }


// Site Search Toggle
// var searchToggle = function(){
//   search_container = document.querySelector(".popout-form");
//   if(search_container){
//     search_container.classList.toggle("active");
//   } else {
//     var elmnt = document.querySelector(".full-header-search");
//     elmnt.scrollIntoView();
//   }
// }

// var search_toggle = document.getElementById('search-toggle');
// search_toggle.addEventListener('click', searchToggle , false);



//Site Menu Dropdown Toggle
var menuDropdown = function(){
  //div inside element contains dropdown menu
  var dropdown = this.parentNode;

  //if it's already active set it not to be
  if(dropdown.classList.contains("active")){
    dropdown.classList.toggle("active");
    return;
  }
  //get current active class
  var activeClass = document.querySelectorAll(".menu-dropdown.active");
  // make it not active
  if(activeClass!=null){
    for(var i=0; i<activeClass.length; i++){
      activeClass[i].classList.toggle("active");
    }
  }

  //apply active to the div inside element that contains dropdown menu
  dropdown.classList.toggle("active");
}

var dropdownTrigger = document.querySelectorAll(".menu-dropdown > a");

for(var i=0; i<dropdownTrigger.length; i++){
  dropdownTrigger[i].addEventListener('click', menuDropdown, false);
}


// Toggle class for accordions
let acc = document.getElementsByClassName('mobile-accordion-header');
for (let i = 0; i < acc.length; i++) {
  acc[i].addEventListener("click", function(ev) {

    for (let i = 0; i < acc.length; i++) {
      if (acc[i] === this || acc[i].parentElement.parentElement !== this.parentElement.parentElement || this.parentElement.classList.contains('no-close')) {
        continue;
      }
      acc[i].parentElement.classList.remove('active');
    }

    if (!this.parentElement.classList.contains('active') && !this.parentElement.classList.contains('no-scroll')){
      setTimeout(() => { document.documentElement.scrollTop += this.getBoundingClientRect().top; }, 500);
    }
    this.parentElement.classList.toggle("active");

  });
}

//Dropdown Menu Toggle
var showMenu = function(){
  //div inside element contains dropdown menu
  var dropdown = this.parentNode.querySelector('div');
  var menuToggle = this;

  //if it's already active set it not to be
  if(dropdown.classList.contains("active")){
    dropdown.classList.toggle("active");
    menuToggle.classList.toggle("active");
    return;
  }
  //get current active class
  var activeClass = document.querySelectorAll(".dropdown-menu .active");
  // make it not active
  if(activeClass!=null){
    for(var i=0; i<activeClass.length; i++){
      activeClass[i].classList.toggle("active");
    }
  }

  //apply active to the div inside element that contains dropdown menu
  dropdown.classList.toggle("active");
  menuToggle.classList.toggle("active");

  // Nav hover
  document.querySelectorAll('*').forEach(value => {
    value.addEventListener('mouseover', (ev) => {
      if (ev.target.classList.contains('site-menu') || ev.target.classList.contains('main-nav') || ev.target.classList.contains('dropdown') || ev.target.classList.contains('tingle-modal-box') || ev.target.classList.contains('tingle-modal-box__content') || ev.target.nodeName === 'LI' || ev.target.nodeName === 'UL' || ev.target.nodeName === 'A') {

      } else {
        dropdown.classList.remove("active");
        menuToggle.classList.remove("active");
      }
    })
  })
}

var menuDropdown = document.querySelectorAll(".dropdown-menu > a");

for(var i=0; i<menuDropdown.length; i++){
  menuDropdown[i].addEventListener('click', showMenu, false);
}

//faqs
var faqSearch = document.querySelector('.faq-search input');

if(faqSearch !== null){
  faqSearch.addEventListener('keyup', function(e){
    var results = document.getElementById('faq-results');
    if(this.value.length != 0){
      results.classList.add("active");
    } else {
      results.classList.remove("active");
    }
  });
}


//More Options Toggle
var classname = document.querySelectorAll(".more-options");

var moreOptions = function(e) {
  e.preventDefault();
  var element = document.getElementById("vehicle-search");
  element.classList.toggle("active");
  if(element.classList.contains('active')){
    this.innerHTML = 'Hide additional options';
  } else {
    this.innerHTML = 'Show me more options';
  }
};

for (var i = 0; i < classname.length; i++) {
  classname[i].addEventListener('click', moreOptions, false);
}

//Tab Content
function openTab(e, tabName) {
  var i, tabcontent, tablinks;
  tabcontent = document.getElementsByClassName("tab-content");
  tablinks = document.getElementsByClassName("tab");

  //remove active tab
  for (i = 0; i < tabcontent.length; i++) {
    tabcontent[i].className = tabcontent[i].className.replace(" active", "");
  }

  //remove active from button tabs
  for (i = 0; i < tablinks.length; i++) {
    tablinks[i].className = tablinks[i].className.replace(" active", "");
  }
  //add active to tab
  document.getElementById(tabName).className += " active";

  //add active to button tabs
  e.currentTarget.className += " active";
}

//Search for Cars or Vans
function showDesc(el){
  el.style.display = 'none';
  el.parentNode.querySelector('.extra').classList.remove('visuallyhidden');
}






/**
 * SEARCH
 */
function searchType(elem, searchType) {
  let hiddenField, searchTab;

  //set hidden field value
  hiddenField = document.getElementById("search-type");
  hiddenField.setAttribute('value', searchType);

  //remove active class
  searchTab = document.getElementsByClassName("search-tab");
  for (i = 0; i < searchTab.length; i++) {
    searchTab[i].className = searchTab[i].className.replace(" active", "");
  }

  //add active class to clicked
  elem.classList.toggle("active");

  loadManufacturers(document.querySelector('#search-type').value);
  loadBodyTypes(document.querySelector('#search-type').value);
  loadSpecs(document.querySelector('#search-type').value);

  var dropdown = document.getElementsByName('model')[0];
  dropdown.length = 0;

  var defaultOption = document.createElement('option');
  defaultOption.text = 'Any Model';
  defaultOption.value = '';

  dropdown.add(defaultOption);
  dropdown.selectedIndex = 0;
}

function loadManufacturers($type) {
  var make_dropdowns = document.getElementsByName("make");
  for (i = 0; i < make_dropdowns.length; i++) {

    var dropdown = make_dropdowns[0];
    dropdown.length = 0;

    var defaultOption = document.createElement('option');
    defaultOption.text = 'Any Make';
    defaultOption.value = '';

    dropdown.add(defaultOption);
    dropdown.selectedIndex = 0;
    if (pure_electric == false) {
      var url = '/api/'+$type+'/manufacturers/available';
    } else {
      var url = '/api/'+$type+'/manufacturers/ev';
    }
    fetch(url).then(function(response) {
      if (response.status !== 200) {
        console.warn('Looks like there was a problem. Status Code: ' +
          response.status);
        return;
      }
      return response.json();
    }).then(function(data) {
      let keysSorted = Object.keys(data).sort(function(a, b) {
        return data[a].localeCompare(data[b])
      });
      for (let x in keysSorted) {
        let option = document.createElement('option');
        option.text = data[keysSorted[x]];
        option.value = keysSorted[x];
        dropdown.add(option);
      }
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.has('make') ? document.querySelector('.search-form [name="make"]').value = urlParams.get('make') : null;
      urlParams.has('make') ? document.querySelector('.search-form [name="make"]').dispatchEvent(new Event('change')) : null;
    }).catch(function(err) {
      console.error('Fetch Error -', err);
    });
  }
}

function loadModels($type, $manufacturer) {

  let defaultOption = document.createElement('option');
  defaultOption.text = 'Any Model';
  defaultOption.value = '';
  let loadingOption = document.createElement('option');
  loadingOption.text = 'Loading...';
  loadingOption.value = '';
  let loadingError = document.createElement('option');
  loadingError.text = 'Loading Error, please check your internet connection';
  loadingError.value = '';

  var model_dropdowns = document.getElementsByName("model");
  for (let i = 0; i < model_dropdowns.length; i++) {
    var dropdown = model_dropdowns[i];
    dropdown.length = 0;
    dropdown.add(defaultOption);
    dropdown.selectedIndex = 0;

    if ($manufacturer) {
      dropdown.length = 0;
      dropdown.add(loadingOption);
      dropdown.selectedIndex = 0;
      if (pure_electric == false) {
        var url = '/api/'+$type+'/models/'+$manufacturer+'/available';
      } else {
        var url = '/api/'+$type+'/models/'+$manufacturer+'/ev';
      }
      fetch(url).then(function(response) {
        if (response.status !== 200) {
          console.warn('Looks like there was a problem. Status Code: ' +
            response.status);
          return;
        }
        return response.json();
      }).then(function(data) {
        dropdown.length = 0;
        dropdown.add(defaultOption);
        dropdown.selectedIndex = 0;
        let keysSorted = Object.keys(data).sort(function(a, b) {
          return data[a].localeCompare(data[b])
        });
        for (let x in keysSorted) {
          let option = document.createElement('option');
          option.text = data[keysSorted[x]];
          option.value = keysSorted[x];
          dropdown.add(option);
        }
        const urlParams = new URLSearchParams(window.location.search);
        const makeParam = urlParams.get('make');
        const modelParam = urlParams.get('model');
        if (modelParam && makeParam == $manufacturer) {
          dropdown.value = modelParam;
          loadSpecs($type, modelParam);
        }
      }).catch(function(err) {
        dropdown.length = 0;
        dropdown.add(loadingError);
        dropdown.selectedIndex = 0;
        console.error('Fetch Error -', err);
      });
    }
  }
}

function loadBodyTypes($type) {
  var dropdown = document.getElementsByName('body_type')[0];

  if(typeof dropdown == 'undefined'){
    return false;
  }

  dropdown.length = 0;

  var defaultOption = document.createElement('option');
  defaultOption.text = 'Any Bodytype';
  defaultOption.value = '';

  dropdown.add(defaultOption);
  dropdown.selectedIndex = 0;

  var url = '/api/'+$type+'/types';

  fetch(url).then(function(response) {
    if (response.status !== 200) {
      console.warn('Looks like there was a problem. Status Code: ' +
        response.status);
      return;
    }
    return response.json();
  }).then(function(data) {
    let keysSorted = Object.keys(data).sort(function(a, b) {
      return data[a].localeCompare(data[b])
    });
    for (let x in keysSorted) {
      let option = document.createElement('option');
      option.text = data[keysSorted[x]];
      option.value = keysSorted[x];
      dropdown.add(option);
    }
  }).catch(function(err) {
    console.error('Fetch Error -', err);
  });
}

function loadSpecs($type, $model) {
  var dropdown = document.getElementsByName('spec')[0];
  if (dropdown){
    let defaultOption = document.createElement('option');
    defaultOption.text = 'Any Spec';
    defaultOption.value = '';
    let loadingOption = document.createElement('option');
    loadingOption.text = 'Loading...';
    loadingOption.value = '';
    let loadingError = document.createElement('option');
    loadingError.text = 'Loading Error, please check your internet connection';
    loadingError.value = '';

    dropdown.length = 0;
    dropdown.add(defaultOption);
    dropdown.selectedIndex = 0;

    if ($model) {
      dropdown.length = 0;
      dropdown.add(loadingOption);
      dropdown.selectedIndex = 0;
      var url = '/api/'+$type+'/specs/'+$model;

      fetch(url).then(function(response) {
        if (response.status !== 200) {
          console.warn('Looks like there was a problem. Status Code: ' +
            response.status);
          return;
        }
        return response.json();
      }).then(function(data) {
        dropdown.length = 0;
        dropdown.add(defaultOption);
        dropdown.selectedIndex = 0;
        let keysSorted = Object.keys(data).sort(function(a, b) {
          return data[a].localeCompare(data[b])
        });
        for (let x in keysSorted) {
          let option = document.createElement('option');
          option.text = data[keysSorted[x]];
          option.value = keysSorted[x];
          dropdown.add(option);
        }
        const urlParams = new URLSearchParams(window.location.search);
        const modelParam = urlParams.get('model');
        const specParam = urlParams.get('spec_level');
        if (specParam && modelParam == $model) {
          dropdown.value = specParam;
        }
      }).catch(function(err) {
        dropdown.length = 0;
        dropdown.add(loadingError);
        dropdown.selectedIndex = 0;
        console.error('Fetch Error -', err);
      });
    }
  }
}

if(document.getElementById('send-contact-form')){

  let button = document.getElementById('send-contact-form');
  let terms = document.querySelector('.contact-form [name=consent]');
  if(terms) {
    terms.onchange = function() {
      button.disabled = !this.checked;
    };
  }

  document.getElementById('send-contact-form').addEventListener("click", function(e) {
    e.preventDefault();
    document.body.classList.remove('loaded');

    grecaptcha.ready(function() {
      grecaptcha.execute(document.getElementById('contact-form').dataset['recaptcha'], {action: 'contact'}).then(function(recaptcha_token) {
        document.querySelector('input[name=g-recaptcha-response]').value = recaptcha_token;

        let url = '/contact';
        let email = document.querySelector('#contact-form [name=email]').value;
        let name = document.querySelector('#contact-form [name=name]').value;
        let telephone = document.querySelector('#contact-form [name=telephone]').value;
        let company = '';
        if (document.querySelector('#contact-form [name=company]')){
          company = document.querySelector('#contact-form [name=company]').value;
        }

        let comments = '';
        if (document.querySelector('#contact-form [name=employer-employee]')) {
          comments = 'Employer or Employee: ' + document.querySelector('#contact-form [name=employer-employee]').value +
            (document.querySelector('#contact-form [name=position]') ? '\n\r Position In Business: ' +
              document.querySelector('#contact-form [name=position]').value : '') +
            (document.querySelector('#contact-form [name=staff-number]') ? '\n\r Number Of Staff: ' +
              document.querySelector('#contact-form [name=staff-number]').value : '') +
            '\n\r Message: ' + document.querySelector('#contact-form [name=comments]').value;
        } else {
          comments = document.querySelector('#contact-form [name=comments]').value;
        }


        let token = document.querySelector('#contact-form [name=_token]').value;
        let recaptcha = document.querySelector('#contact-form input[name=g-recaptcha-response]').value;

        fetch(url, {
          method: 'POST',
          body: JSON.stringify({
            'name': name,
            'email': email,
            'telephone': telephone,
            'company': company,
            'comments': comments,
            '_token': token,
            'g-recaptcha-response': recaptcha
          }),
          headers:{
            'Content-Type': 'application/json'
          }}).then(function(response) {
          document.body.classList.add('loaded');
          if (response.status !== 200) {
            console.warn('Looks like there was a problem. Status Code: ' + response.status);
            return;
          }
          return response.json();
        }).then(function(data) {
          if(data.status!='ok'){
            let errors = '';
            for (let i in data.errors) {
              errors += data.errors[i][0]+'\n';
            }
            return alert('There was a problem with your form\n\n'+errors);
          }

          document.getElementsByClassName('contact-form')[0].reset();
          document.getElementsByClassName('contact-content')[0].classList.add('hidden');
          document.getElementsByClassName('thank-you')[0].classList.remove('hidden');
          document.getElementsByClassName('contact-details-form')[0].scrollIntoView();

          // Trigger event tracking
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            'event': 'Contact Form Submit'
          });

        }).catch(function(err) {
          document.body.classList.add('loaded');
          console.error('Fetch Error -', err);
          return alert('There was a problem sending your enquiry. Please call us or use our general contact email address.');
        });

      });
    });

  });
}

if(document.getElementById('email-signup')){
  let button_2 = document.querySelector('#email-signup');
  let terms = document.querySelector('.newsletter-form [name=consent]');
  if(terms) {
    terms.onchange = function() {
      button_2.disabled = !this.checked;
    };
  }

  document.getElementById('email-signup').addEventListener("click", function(e) {
    e.preventDefault();

    let recaptcha_token = null;
    recaptcha_token = document.querySelector('input[name=g-recaptcha-response]').value;

    document.getElementById('email-signup').classList.add('preload');
    document.getElementById('email-signup').innerHTML = 'Please wait';
    document.getElementById('email-signup').disabled = true;

    let url = '/newsletter';
    let email = document.getElementById('newsletter-email').value;
    let name = document.getElementById('newsletter-forename').value;
    let surname = document.getElementById('newsletter-surname').value;
    let token = document.getElementsByName('_token')[0].value;

    fetch(url, {
      method: 'POST',
      body: JSON.stringify({ 'name': `${name} ${surname}`, 'first_name': name, 'last_name': surname, 'email': email, 'mail_status': 'subscribed', '_token': token }),
      headers:{
        'Content-Type': 'application/json'
      }}).then(function(response) {
      if (response.status !== 200) {
        console.warn('Looks like there was a problem. Status Code: ' +
          response.status);
        return;
      }
      return response.json();
    }).then(function(data) {
      document.getElementById('email-signup').classList.remove('preload');
      document.getElementById('email-signup').innerHTML = 'Signup';
      document.getElementById('email-signup').disabled = false;

      if(data.status!='ok'){
        let errors = '';
        for (let i in data.errors) {
          errors += data.errors[i][0]+'\n';
        }
        if(typeof Swal !== 'undefined'){
          return Swal.fire({
            title: 'Whoops!',
            html: ('There was a problem with your sign up details\n\n'+errors).replace(/\n/gi,'<br>'),
            icon: 'error',
            confirmButtonText: 'OK',
          });
        } else {
          return alert('There was a problem with your sign up details\n\n'+errors);
        }
      }

      document.getElementsByClassName('newsletter-form')[0].reset();
      // Trigger event tracking
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'Newsletter Form Submit'
      });

      if(typeof Swal !== 'undefined'){
        return Swal.fire({
          title: 'Thank you!',
          text: 'You\'re signed up!',
          icon: 'success',
          confirmButtonText: 'OK'
        });
      } else {
        return alert('Thank you!');
      }

    }).catch(function(err) {
      console.error('Fetch Error -', err);
      document.getElementById('email-signup').classList.remove('preload');
      document.getElementById('email-signup').innerHTML = 'Signup';
      document.getElementById('email-signup').disabled = false;
    });
  });
}



document.addEventListener("DOMContentLoaded", function() {
  //Lazy Load Images
  let lazyImages = [].slice.call(document.querySelectorAll("img.lazy"));
  let active = false;

  const lazyLoad = function() {
    if (active === false) {
      active = true;


      lazyImages.forEach(function(lazyImage) {
        if ((lazyImage.getBoundingClientRect().top <= window.innerHeight && lazyImage.getBoundingClientRect().bottom >= 0) && getComputedStyle(lazyImage).display !== "none") {
          lazyImage.src = lazyImage.dataset.src;
          // lazyImage.srcset = lazyImage.dataset.srcset;
          lazyImage.classList.remove("lazy");

          lazyImages = lazyImages.filter(function(image) {
            return image !== lazyImage;
          });

          if (lazyImages.length === 0) {
            document.removeEventListener("scroll", lazyLoad);
            window.removeEventListener("resize", lazyLoad);
            window.removeEventListener("orientationchange", lazyLoad);
          }
        }
      });

      active = false;
    }
  };

  document.addEventListener("scroll", lazyLoad);
  window.addEventListener("resize", lazyLoad);
  window.addEventListener("orientationchange", lazyLoad);
  lazyLoad();

  // Derivative Gallery
  const dealGallerySlider = document.querySelector('.deal-gallery-track');
  if (dealGallerySlider) {
    new Glider(dealGallerySlider, {
      slidesToScroll: 1,
      slidesToShow: 2,
      draggable: true,
      scrollLock: true,
      arrows: {
        prev: '.deal-gallery-controls .glider-prev',
        next: '.deal-gallery-controls .glider-next'
      },
      responsive: [
        {
          breakpoint: 767,
          settings: {
            slidesToShow: 4,
          }
        },
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 2,
          }
        },
        {
          breakpoint: 1200,
          settings: {
            slidesToShow: 4,
          }
        },
        {
          breakpoint: 1280,
          settings: {
            slidesToShow: 2,
          }
        },
        {
          breakpoint: 1600,
          settings: {
            slidesToShow: 3,
          }
        },
        {
          breakpoint: 1900,
          settings: {
            slidesToShow: 4,
          }
        }
      ]
    })
  }
});

window.onload = (function(){

  //preloader
  var root = document.getElementsByTagName( 'html' )[0];
  root.classList.add("site-loaded");
  document.body.classList.add("loaded");

  // Load Search Form
  if(document.getElementsByClassName('search-form').length>0){
    let $type = document.getElementById('search-type').getAttribute('value').toLowerCase();
    loadManufacturers($type);

    // Search
    if(document.querySelectorAll('.search-form [name=make] option').length<=1){
      $type = document.getElementById('search-type').getAttribute('value').toLowerCase();
      // loadManufacturers($type);
      loadBodyTypes($type);
      if(document.querySelectorAll('.search-form [name=spec] option').length<=1){
        loadSpecs($type, document.querySelector('.search-form [name=model]').value);
      }
    }
    if(document.querySelectorAll('.search-form [name=model] option').length<=1){
      loadModels($type, document.querySelector('.search-form [name=make]').value);
    }
    if(document.querySelectorAll('.search-form [name=spec] option').length<=1){
      loadSpecs($type, document.querySelector('.search-form [name=model]').value);
    }

    document.querySelector('.search-form [name=make]').addEventListener("change", function(e){
      $type = document.getElementById('search-type').getAttribute('value').toLowerCase();
      loadModels($type, e.target.value);
      loadSpecs($type);
    });

    document.querySelector('.search-form [name=model]').addEventListener("change", function(e){
      $type = document.getElementById('search-type').getAttribute('value').toLowerCase();
      loadSpecs($type, e.target.value);
    });
  }

  // Indicative Fuel Cost
  function indicativeCost($fuel_type = false, $mpg = null, $electric_range = null, $battery_size = null, $electricity_cost = null) {
    let indicativeCost;
    if ($fuel_type === 'Electric' && $electric_range && $battery_size){
      let electricity_cost = ($electricity_cost) ? $electricity_cost : document.getElementById('average-data').dataset.averageElectricity;
      let miles_per_kwh = $electric_range / $battery_size;
      let cost_per_mile = electricity_cost / miles_per_kwh;
      indicativeCost = cost_per_mile.toFixed(1);
    } else if ($fuel_type === 'Diesel' && $mpg) {
      let cost_per_litre_diesel = document.getElementById('average-data').dataset.averageDiesel;
      let miles_per_litre = $mpg / 4.54609;
      let cost_per_mile = cost_per_litre_diesel / miles_per_litre;
      indicativeCost = cost_per_mile.toFixed(1);
    } else if ($fuel_type !== false && $mpg) {
      let cost_per_litre_petrol = document.getElementById('average-data').dataset.averagePetrol;
      let miles_per_litre = $mpg / 4.54609;
      let cost_per_mile = cost_per_litre_petrol / miles_per_litre;
      indicativeCost = cost_per_mile.toFixed(1);
    } else {
      indicativeCost = 'N/A';
    }
    return indicativeCost;
  }

// Calculators
  if (document.querySelector('body').classList.contains('page-tax-calculator') || document.querySelector('body').classList.contains('page-journey-cost-calculator') || document.querySelector('body').classList.contains('page-compare-vehicles') || document.querySelector('body').classList.contains('page-charging-calculator')) {

    // Next Step
    document.querySelectorAll('.next-step').forEach((value) => {
      value.addEventListener('click', (e) => {
        document.querySelectorAll('.step').forEach((value) => { value.classList.remove('active') })
        value.parentElement.parentElement.nextElementSibling.classList.add('active')
      })
    })
    // Back Step
    document.querySelectorAll('.back-step').forEach((value) => {
      value.addEventListener('click', (e) => {
        document.querySelectorAll('.step').forEach((value) => { value.classList.remove('active') })
        value.parentElement.parentElement.previousElementSibling.classList.add('active')
      })
    })

    // Steps Progress Bar
    document.querySelectorAll('.next-step, .back-step').forEach((value) => {
      value.addEventListener('click', (e) => {
        let percent = value.dataset.percent;
        document.querySelector('#progress').value = (percent == 0) ? 1 : percent;
        document.querySelector('#progress-text').innerHTML = percent;
      })
    })
    
  }
  // Load Manufacturers Dropdown
  function loadManufacturersCalc() {
    var make_dropdowns = document.getElementsByName("calculator-make");
    for (i = 0; i < make_dropdowns.length; i++) {

      var dropdown = make_dropdowns[0];
      dropdown.length = 0;

      var defaultOption = document.createElement('option');
      defaultOption.text = 'Select Manufacturer';
      defaultOption.value = '';

      dropdown.add(defaultOption);
      dropdown.selectedIndex = 0;

      var url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/car/manufacturers';

      fetch(url).then(function(response) {
        if (response.status !== 200) {
          console.warn('Looks like there was a problem. Status Code: ' +
            response.status);
          return;
        }
        return response.json();
      }).then(function(data) {
        let keysSorted = Object.keys(data).sort(function(a, b) {
          return data[a].localeCompare(data[b])
        });
        for (let x in keysSorted) {
          let option = document.createElement('option');
          option.text = data[keysSorted[x]];
          option.value = keysSorted[x];
          dropdown.add(option);
        }
      }).catch(function(err) {
        console.error('Fetch Error -', err);
      });
    }
  }
  // Load Models Dropdown
  function loadModelsCalc($type, $manufacturer, $ev = true, $inactive = false, $compare = false) {
    let model_dropdowns, derivative_dropdowns;
    ($compare) ? model_dropdowns = document.getElementsByName("calculator-model-compare") : model_dropdowns = document.getElementsByName("calculator-model");
    ($compare) ? derivative_dropdowns = document.getElementsByName("calculator-derivative-compare") : derivative_dropdowns = document.getElementsByName("calculator-derivative");
    for (let i = 0; i < derivative_dropdowns.length; i++) {
      let dropdown = derivative_dropdowns[i];
      dropdown.length = 0;
      let defaultOption = document.createElement('option');
      defaultOption.text = 'Select Variant';
      defaultOption.value = '';
      dropdown.add(defaultOption);
      dropdown.selectedIndex = 0;
    }
    for (let i = 0; i < model_dropdowns.length; i++) {
      let dropdown = model_dropdowns[i];
      dropdown.length = 0;

      let defaultOption = document.createElement('option');
      defaultOption.text = 'Select Model';
      defaultOption.value = '';

      dropdown.add(defaultOption);
      dropdown.selectedIndex = 0;

      let url;
      ($ev) ? url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/' + $type + '/models/' + $manufacturer + '' : url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/' + $type + '/models/' + $manufacturer;

      let params;
      ($inactive) ? params = '?' + new URLSearchParams({'inc': 'inactive'}) : params = '';

      fetch(url + params).then(function(response) {
        if (response.status !== 200) {
          console.warn('Looks like there was a problem. Status Code: ' +
            response.status);
          return;
        }
        return response.json();
      }).then(function(data) {
        let keysSorted = Object.keys(data).sort(function(a, b) {
          return data[a].localeCompare(data[b])
        });
        for (let x in keysSorted) {
          let option = document.createElement('option');
          option.text = data[keysSorted[x]];
          option.value = keysSorted[x];
          dropdown.add(option);
        }
      }).catch(function(err) {
        console.error('Fetch Error -', err);
      });
    }
  }
  // Load Derivatives Dropdown
  function loadDerivativeCalc($type, $model, $default = false, $ev = true, $inactive = false, $compare = false) {
    let derivative_dropdowns;
    ($compare) ? derivative_dropdowns = document.getElementsByName("calculator-derivative-compare") : derivative_dropdowns = document.getElementsByName("calculator-derivative");
    for (let i = 0; i < derivative_dropdowns.length; i++) {
      let dropdown = derivative_dropdowns[i];
      dropdown.length = 0;

      if ($default){
        let defaultOption = document.createElement('option');
        defaultOption.text = 'Select Variant';
        defaultOption.value = '';

        dropdown.add(defaultOption);
        dropdown.selectedIndex = 0;
      }

      let url;
      ($ev) ? url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/' + $type + '/derivatives/' + $model + '' : url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/' + $type + '/derivatives/' + $model;

      let params;
      if($compare) {
        ($inactive) ? params = '?' + new URLSearchParams({'inc': 'inactive', 'compare_ev': 'true'}) : params = '';
      } else {
        ($inactive) ? params = '?' + new URLSearchParams({'inc': 'inactive'}) : params = '';
      }

      fetch(url + params).then(function(response) {
        if (response.status !== 200) {
          console.warn('Looks like there was a problem. Status Code: ' +
            response.status);
          return;
        }
        return response.json();
      }).then(function(data) {
        let keysSorted = Object.keys(data).sort(function(a, b) {
          return data[a].localeCompare(data[b])
        });
        for (let x in keysSorted) {
          if (x === '2813'){ continue }
          let option = document.createElement('option');
          option.text = data[keysSorted[x]];
          option.value = keysSorted[x];
          dropdown.add(option);
        }
      }).catch(function(err) {
        console.error('Fetch Error -', err);
      });
    }
  }

  // Journey Cost Calculator
  if (document.querySelector('body').classList.contains('page-journey-cost-calculator')) {
    loadManufacturersCalc();
    let makeField = document.querySelector('select[name=calculator-make]')
    let modelField = document.querySelector('select[name=calculator-model]')
    let derivativeField = document.querySelector('select[name=calculator-derivative]')
    let makeFieldCompare = document.querySelector('select[name=calculator-make-compare]')
    let modelFieldCompare = document.querySelector('select[name=calculator-model-compare]')
    let derivativeFieldCompare = document.querySelector('select[name=calculator-derivative-compare]')
    let milesField = document.querySelector('input[name=journey-calculator-miles]')
    let electricityField = document.querySelector('input[name=journey-calculator-electricity]')
    let electricityLabel = document.querySelector('.electricity-cost-label')
    let makeText = document.querySelectorAll('.step-four-make')
    let modelText = document.querySelectorAll('.step-four-model')
    let stepOneNext = document.querySelector('#step-one-next')
    let stepTwoNext = document.querySelector('#step-two-next')
    let averageValue, charger_rate, journey_distance, journey_cost, annual_journey_cost, co2, img_url, electric_range, battery_capacity;
    let comparable_london, comparable_ulez, comparable_journey_cost, comparable_annual_journey_cost, comparable_co2, comparable_img_url, comparable_mpg, comparable_type;
    let ev_loaded, ice_loaded = false;

    function loadVehicle($derivative, $ev = false) {
      let url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/car/' + $derivative;
      fetch(url).then(function(response) {
        if (response.status !== 200) {
          console.warn('Looks like there was a problem. Status Code: ' + response.status);
          return;
        }
        return response.json();
      }).then(function(data) {
        // console.log(data)
        let getCapTechnicalData = ($category, $term) => (data.car.technical[$category] !== undefined) ? (data.car.technical[$category].find(item => item.name.includes($term))) : null;
        if ($ev) {
          // nox = ((getCapTechnicalData('Emissions - ICE', 'NOx') != null && isNaN(getCapTechnicalData('Emissions - ICE', 'NOx') != 'Not Available'))) ? 'Not Available': getCapTechnicalData('Emissions - ICE', 'NOx').value + 'mg';
          electric_range = (data.car.electric_range != null) ? data.car.electric_range : ((data.car.technical) ? ((getCapTechnicalData('Electric Vehicle Data', 'Electric Range (miles)')) ? parseFloat(getCapTechnicalData('Electric Vehicle Data', 'Electric Range (miles)').value) : null) : null);
          battery_capacity = data.car.battery_capacity;
          // miles_per_kWh = (getCapTechnicalData('Electric Vehicle Data', 'WLTP - EC (miles/kWh) - Comb')) ? getCapTechnicalData('Electric Vehicle Data', 'WLTP - EC (miles/kWh) - Comb').value : ( (getCapTechnicalData('Electric Vehicle Data', 'WLTP - EC (kWh/100km) - Comb')) ? ( 100 / 1.609344 / parseFloat(getCapTechnicalData('Electric Vehicle Data', 'WLTP - EC (kWh/100km) - Comb').value)) : null);
          co2 = (data.car.co2) ? data.car.co2 : 0;
          img_url = data.car.main_img;
          // comparable_car = data.car.non_ev_comparable_car;
          // setTimeout(() => { loadVehicle(data.car.non_ev_comparable_car, false) }, 500);
          setTimeout(() => {
            if (electric_range && battery_capacity) {
              ev_loaded = true;
              document.getElementById('no-data').classList.add('visuallyhidden');
              (derivativeField.childElementCount > 0 && derivativeFieldCompare.childElementCount > 0 && ice_loaded) ? stepOneNext.classList.remove('disabled') : stepOneNext.classList.add('disabled');
            } else {
              ev_loaded = false;
              stepOneNext.classList.add('disabled');
              document.getElementById('no-data').classList.remove('visuallyhidden');
            }
          }, 1000);

          let compareButton = document.querySelector('button[data-compare]');
          if (compareButton) {
            compareButton.dataset.compare = data.car.id;
            compareButton.dataset.name = data.car.model_name;
            compareButton.setAttribute('title', `Compare ${data.car.model_name} deal`);
            compareButton.innerHTML = `<svg width="11.8" height="11.8"><use xlink:href="${$base_path}/themes/bounce/img/plus.svg#plus"></use></svg>&nbsp;Compare`;
            compareButton.dataset.compareAdded = 'false';
            let cookie = Cookies.get('compare_list');
            if (cookie) {
              let cookie_obj = JSON.parse(cookie);
              if (cookie_obj[compareButton.dataset.compare] !== undefined) {
                compareButton.innerHTML = `<svg width="9.8" height="9.8"><use xlink:href="${$base_path}/themes/bounce/img/minus.svg#minus"></use></svg>&nbsp;Remove`;
                compareButton.dataset.compareAdded = 'true';
              }
            }
          }

        } else {
          comparable_type = data.car.fuel_type;
          if (comparable_type === 'Electric') {
            setTimeout(() => {
              ice_loaded = false;
              stepOneNext.classList.add('disabled');
              document.getElementById('no-ice-two').classList.remove('visuallyhidden');
            }, 500);
          } else {
            // comparable_nox = ((getCapTechnicalData('Emissions - ICE', 'NOx') != null && isNaN(getCapTechnicalData('Emissions - ICE', 'NOx') != 'Not Available'))) ? 'Not Available': getCapTechnicalData('Emissions - ICE', 'NOx').value + 'mg';
            comparable_img_url = data.car.main_img;
            comparable_co2 = (data.car.co2) ? data.car.co2 : 0;
            comparable_mpg = (data.car.mpg_combined != null) ? data.car.mpg_combined : ((data.car.mpg_urban != null) ? data.car.mpg_urban : data.car.mpg_extra);
            if (comparable_type === 'Petrol') {
              comparable_london = (data.car.tax_band.london != null) ? data.car.tax_band.london : 0;
              comparable_ulez = (getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions')) ? ((parseInt(getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions').value.slice(-1)) >= 4) ? 12.50 : 0) : 0;
            } else if (comparable_type === 'Diesel') {
              comparable_london = (data.car.tax_band.london != null) ? data.car.tax_band.london : 0;
              comparable_ulez = (getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions')) ? ((parseInt(getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions').value.slice(-1)) >= 6) ? 12.50 : 0) : 0;
            } else {
              comparable_london = (data.car.tax_band.london != null) ? data.car.tax_band.london : 0;
              comparable_ulez = 0;
            }
            setTimeout(() => {
              if (comparable_mpg && comparable_co2) {
                ice_loaded = true;
                document.getElementById('no-data-two').classList.add('visuallyhidden');
                document.getElementById('no-ice-two').classList.add('visuallyhidden');
                (derivativeField.childElementCount > 0 && derivativeFieldCompare.childElementCount > 0 && ev_loaded) ? stepOneNext.classList.remove('disabled') : stepOneNext.classList.add('disabled');
              } else {
                ice_loaded = false;
                stepOneNext.classList.add('disabled');
                document.getElementById('no-data-two').classList.remove('visuallyhidden');
              }
            }, 1000);
          }
        }
      }).catch(function(err) {
        console.error('Fetch Error -', err);
      });
    }

    // First Step
    makeField.addEventListener('change', () => {
      (makeField.value > 0) ? loadModelsCalc('car', makeField.value) : null;
      (modelField.value > 0) ? stepOneNext.classList.remove('disabled') : stepOneNext.classList.add('disabled')
      derivativeField.classList.add('visuallyhidden')
      Array.prototype.slice.call(makeText, 0).forEach((value) => {
        value.innerHTML = makeField.options[makeField.selectedIndex].text
      })
      let buttonUrl = $base_path+'/cars/' + makeField.options[makeField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase()
      document.querySelector('.view-range-button').setAttribute('href', buttonUrl)

      document.getElementById('no-data').classList.add('visuallyhidden')
      stepOneNext.classList.add('disabled')
    })
    modelField.addEventListener('change', () => {
      (modelField.value > 0) ? loadDerivativeCalc('car', modelField.value, false, true, true, false) : null; // inactive reversed

      Array.prototype.slice.call(modelText, 0).forEach((value) => {
        value.innerHTML = modelField.options[modelField.selectedIndex].text
      })
      let buttonUrl = $base_path+'/cars/' + makeField.options[makeField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase() + '/' + modelField.options[modelField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase()
      document.querySelector('.view-range-button').setAttribute('href', buttonUrl)
      derivativeField.classList.remove('visuallyhidden')

      document.getElementById('no-data').classList.add('visuallyhidden')
      stepOneNext.classList.add('disabled')

      setTimeout(() => { (derivativeField.value > 0) ? loadVehicle(derivativeField.value, true) : null }, 500);
    })
    derivativeField.addEventListener('change', () => {
      stepOneNext.classList.add('disabled');
      document.getElementById('no-data').classList.add('visuallyhidden');
      (derivativeField.value > 0) ? loadVehicle(derivativeField.value, true) : null;
    })

    // Second Step
    makeFieldCompare.addEventListener('change', () => {
      (makeFieldCompare.value > 0) ? loadModelsCalc('car', makeFieldCompare.value, false, true, true) : null;
      (modelFieldCompare.value > 0) ? stepOneNext.classList.remove('disabled') : stepOneNext.classList.add('disabled')
      derivativeFieldCompare.classList.add('visuallyhidden')
      document.querySelector('.step-four-make-second').innerHTML = makeFieldCompare.options[makeFieldCompare.selectedIndex].text;
      document.getElementById('no-data-two').classList.add('visuallyhidden')
      stepOneNext.classList.add('disabled')
    })
    modelFieldCompare.addEventListener('change', () => {
      (modelFieldCompare.value > 0) ? loadDerivativeCalc('car', modelFieldCompare.value, false, false, true, true) : null;
      document.querySelector('.step-four-model-second').innerHTML = modelFieldCompare.options[modelFieldCompare.selectedIndex].text;
      derivativeFieldCompare.classList.remove('visuallyhidden')

      document.getElementById('no-data-two').classList.add('visuallyhidden')
      stepOneNext.classList.add('disabled')

      setTimeout(() => { (derivativeFieldCompare.value > 0) ? loadVehicle(derivativeFieldCompare.value) : null }, 500);
    })
    derivativeFieldCompare.addEventListener('change', () => {
      stepOneNext.classList.add('disabled');
      document.getElementById('no-data-two').classList.add('visuallyhidden');
      (derivativeFieldCompare.value > 0) ? loadVehicle(derivativeFieldCompare.value) : null;
    })

    // Third Step
    milesField.addEventListener('input', () => {
      (milesField.value > 0 && milesField.value <= 9999) ? stepTwoNext.classList.remove('disabled') : stepTwoNext.classList.add('disabled')
    })
    document.querySelector('#route-button-auto').addEventListener('click', (e) => {
      document.querySelector('#route-button-auto').classList.remove('button-outline')
      document.querySelector('#route-button-manual').classList.add('button-outline')
      document.querySelector('.route-auto').classList.remove('visuallyhidden')
      document.querySelector('.route-manual').classList.add('visuallyhidden')
    })
    document.querySelector('#route-button-manual').addEventListener('click', (e) => {
      document.querySelector('#route-button-auto').classList.add('button-outline')
      document.querySelector('#route-button-manual').classList.remove('button-outline')
      document.querySelector('.route-auto').classList.add('visuallyhidden')
      document.querySelector('.route-manual').classList.remove('visuallyhidden')
    })

    // Fourth Step
    electricityField.addEventListener('input', () => {
      if(electricityField.value > 0 && electricityField.value <= electricityField.getAttribute('max')){
        document.querySelector('#step-three-next').classList.remove('disabled')
      } else {
        document.querySelector('#step-three-next').classList.add('disabled')
      }
    })
    document.querySelector('#electricity-cost-button-auto').addEventListener('click', (e) => {
      // document.querySelector('#electricity-cost-button-auto').classList.remove('button-outline')
      // document.querySelector('#electricity-cost-button-manual').classList.add('button-outline')
      // document.querySelector('#electricity-cost-average').classList.remove('visuallyhidden')
      // electricityField.classList.add('visuallyhidden')
      // electricityLabel.classList.add('visuallyhidden')
      // electricityField.value = document.querySelector('#electricity-cost').getAttribute('data-average')
      // electricityField.dispatchEvent(new Event('input'))
      (charger_rate == '150') ? averageValue = document.querySelector('#average-data').getAttribute('data-average-electricity-ultra') : ( (charger_rate == '50') ? averageValue = document.querySelector('#average-data').getAttribute('data-average-electricity-rapid') : averageValue = document.querySelector('#average-data').getAttribute('data-average-electricity') );
      document.querySelector('#electricity-cost').setAttribute('data-average', averageValue);
      electricityField.value = averageValue
      electricityField.dispatchEvent(new Event('input'))
    })

    // document.querySelector('#electricity-cost-button-manual').addEventListener('click', (e) => {
    //   document.querySelector('#electricity-cost-button-auto').classList.add('button-outline')
    //   document.querySelector('#electricity-cost-button-manual').classList.remove('button-outline')
    //   document.querySelector('#electricity-cost-average').classList.add('visuallyhidden')
    //   electricityField.classList.remove('visuallyhidden')
    //   electricityLabel.classList.remove('visuallyhidden')
    //   electricityField.dispatchEvent(new Event('input'))
    // })

    // Reset Form
    function resetForm() {
      let compareButton = document.querySelector('button[data-compare]');
      if (compareButton) {
        compareButton.dataset.compare = '';
        compareButton.dataset.name = '';
        compareButton.setAttribute('title', `Compare deal`);
        compareButton.innerHTML = `<svg width="11.8" height="11.8"><use xlink:href="${$base_path}/themes/bounce/img/plus.svg#plus"></use></svg>&nbsp;Compare`;
        compareButton.dataset.compareAdded = 'false';
      }

      document.querySelector('#progress').value = 1;
      document.querySelector('#progress-text').innerHTML = '0';

      document.getElementById('car-image').setAttribute('src', $base_path+'/themes/bounce/img/placeholder.png');
      document.getElementById("journey-calculator-form").reset();
      derivativeField.classList.add('visuallyhidden')
      derivativeFieldCompare.classList.add('visuallyhidden')
      document.querySelectorAll('.journey-calculator-form > .step').forEach((value) => {
        value.classList.remove('active')
        value.classList.contains('step-one') ? value.classList.add('active') : null;
      })
      document.querySelectorAll('.next-step').forEach((value) => { value.classList.add('disabled') })
      document.getElementById('journey-calculator-route-text').innerText = '';
    }
    setTimeout(resetForm, 500);
    document.querySelectorAll('.start-again').forEach((value) => {
      value.addEventListener('click', resetForm)
    })

    // Calculate
    document.querySelector('#step-three-next').addEventListener('click', (e) => {
      let journey_frequency = document.querySelector('input[name="frequency"]:checked').value;
      let london_frequency = document.querySelector('input[name="london"]:checked').value;
      let ulez_frequency = document.querySelector('input[name="ulez"]:checked').value;
      let pence_per_kwh = electricityField.value;
      journey_distance = milesField.value;

      // First Car
      document.getElementById('car-image').setAttribute('src', img_url);
      document.getElementById('summary-co2').innerText = co2 + 'g/km';
      // document.getElementById('summary-nox').innerText = nox;

      let indicative_fuel_cost = indicativeCost('Electric', null, electric_range, battery_capacity, pence_per_kwh)
      document.getElementById('summary-per-mile').innerText = parseFloat(indicative_fuel_cost).toFixed(1) + 'p'

      journey_cost = (journey_distance * indicative_fuel_cost) / 100; // journey cost (pound)
      document.getElementById('summary-journey').innerText = '£' + journey_cost.toFixed(2);

      annual_journey_cost = journey_frequency * journey_cost;
      document.getElementById('summary-annual').innerText = '£' + annual_journey_cost.toFixed(2)
      document.getElementById('summary-monthly').innerText = '£' + (annual_journey_cost/12).toFixed(2)

      // Second Car
      // document.getElementById('car-image-second').setAttribute('src', comparable_img_url);
      document.getElementById('summary-co2-second').innerText = comparable_co2 + 'g/km';
      // document.getElementById('summary-nox-second').innerText = comparable_nox;

      let comparable_indicative_fuel_cost = indicativeCost(comparable_type, comparable_mpg, false, false, pence_per_kwh);
      document.getElementById('summary-per-mile-second').innerText = parseFloat(comparable_indicative_fuel_cost).toFixed(1) + 'p';

      comparable_journey_cost = (journey_distance * comparable_indicative_fuel_cost) / 100; // journey cost (pound)
      document.getElementById('summary-journey-second').innerText = '£' + comparable_journey_cost.toFixed(2);

      comparable_annual_journey_cost = journey_frequency * comparable_journey_cost;
      document.getElementById('summary-annual-second').innerText = '£' + comparable_annual_journey_cost.toFixed(2)
      document.getElementById('summary-monthly-second').innerText = '£' + (comparable_annual_journey_cost/12).toFixed(2)

      document.getElementById('summary-london-daily-second').innerText = '£' + ((london_frequency == 0) ? 0 : comparable_london);
      document.getElementById('summary-ulez-daily-second').innerText = '£' + ((ulez_frequency == 0) ? 0 : comparable_ulez).toFixed(2);
      document.getElementById('summary-london-annual-second').innerText = '£' + (london_frequency * comparable_london);
      document.getElementById('summary-ulez-annual-second').innerText = '£' + (ulez_frequency * comparable_ulez).toFixed(2);
      // let comparable_journey_cost = (journey_distance * comparable_indicative_cost) / 100; // journey cost (pound)
      // let comparable_annual_journey_cost = journey_frequency * comparable_journey_cost;
      // let comparable_three_year_saving = (comparable_annual_journey_cost - annual_journey_cost) * 3;
      // document.getElementById('summary-estimated-saving').innerText = '£' + comparable_three_year_saving.toFixed(0)

      let total_journey_saving = ((comparable_journey_cost - journey_cost) + ((london_frequency == 0) ? 0 : comparable_london) + ((ulez_frequency == 0) ? 0 : comparable_ulez));
      let total_annual_saving = (total_journey_saving * journey_frequency);
      let total_co2_saving = (comparable_co2 * journey_frequency) + 'g/km';
      // let total_three_year_saving = (((comparable_journey_cost - journey_cost) * journey_frequency) * 3);

      document.getElementById('summary-savings-total').innerText = '£' + total_journey_saving.toFixed(2);
      document.getElementById('summary-savings-annual').innerText = '£' + total_annual_saving.toFixed(2);
      document.getElementById('summary-savings-co2').innerText = total_co2_saving;
      // document.getElementById('summary-estimated-saving').innerText = '£' + total_three_year_saving.toFixed(2);
    })
  }

  // Charging Time & Cost Calculator
  if (document.querySelector('body').classList.contains('page-charging-calculator')) {
    loadManufacturersCalc();
    let makeField = document.querySelector('select[name=calculator-make]')
    let modelField = document.querySelector('select[name=calculator-model]')
    let derivativeField = document.querySelector('select[name=calculator-derivative]')
    let electricityField = document.querySelector('input[name=charging-calculator-electricity]')
    // let electricityLabel = document.querySelector('.electricity-cost-label')
    let rateSwitch = document.querySelectorAll('input[name=speed]')
    let stepOneNext = document.querySelector('#step-one-next')
    let slider = document.getElementById('chargePercentage');
    let output = document.getElementById('chargePercentageNumber');
    let button_url, electric_range, averageValue, charger_speed, charging_time, slow_kw, fast_kw, rapid_kw, ultra_kw, slow_time, fast_time, rapid_time, ultra_time, img_url, charger_rate, battery_capacity;

    function loadVehicle($derivative) {
      let url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/car/' + $derivative;
      fetch(url).then(function(response) {
        if (response.status !== 200) {
          console.warn('Looks like there was a problem. Status Code: ' + response.status);
          return;
        }
        return response.json();
      }).then(function(data) {
        let getCapTechnicalData = ($category, $term) => (data.car.technical[$category] !== undefined) ? (data.car.technical[$category].find(item => item.name.includes($term))) : false;
        img_url = data.car.main_img;
        battery_capacity = data.car.battery_capacity;
        electric_range = (data.car.electric_range != null) ? data.car.electric_range : ((data.car.technical) ? ((getCapTechnicalData('Electric Vehicle Data', 'Electric Range (miles)')) ? parseFloat(getCapTechnicalData('Electric Vehicle Data', 'Electric Range (miles)').value) : null) : null);
        // (getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Slow kW')) ? slow_kw = getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Slow kW').value : slow_kw = null;
        // (getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Fast kW')) ? fast_kw = getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Fast kW').value : fast_kw = null;
        // (getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Slow Time')) ? slow_time = getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Slow Time').value : slow_time = null;
        // (getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Fast Time')) ? fast_time = getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Fast Time').value : fast_time = null;
        // (getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Rapid kW')) ? rapid_kw = getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Rapid kW').value : rapid_kw = null;
        // (getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Rapid Time')) ? rapid_time = getCapTechnicalData('Electric Vehicle Data', 'Battery Charge Rapid Time').value : rapid_time = null;
        slow_kw = 7;
        fast_kw = 22;
        rapid_kw = 60;
        ultra_kw = 350;
        slow_time = (battery_capacity / slow_kw)*60;
        fast_time = (battery_capacity / fast_kw)*60;
        rapid_time = (battery_capacity / rapid_kw)*60;
        ultra_time = (battery_capacity / ultra_kw)*60;

        setTimeout(() => {
          if (electric_range && slow_kw && battery_capacity && slow_time) {
            document.getElementById('car-image').setAttribute('src', img_url);
            document.getElementById('no-data').classList.add('visuallyhidden');
            document.getElementById('speed-slow').setAttribute('value', slow_kw);
            document.querySelector('#speed-slow-label > span').innerText = slow_kw;
            (derivativeField.childElementCount > 0) ? stepOneNext.classList.remove('disabled') : stepOneNext.classList.add('disabled');
            // button_url = $base_path+'/cars/' + makeField.options[makeField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase() + '/' + modelField.options[modelField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase()
            button_url = $base_path+data.car.full_url
            document.querySelector('.view-range-button').setAttribute('href', button_url)
            document.querySelector('button[data-compare]').setAttribute('data-compare', data.car.id)

            Array.prototype.slice.call(document.querySelectorAll('.step-four-make'), 0).forEach((value) => {
              value.innerHTML = makeField.options[makeField.selectedIndex].text
            })
            Array.prototype.slice.call(document.querySelectorAll('.step-four-model'), 0).forEach((value) => {
              value.innerHTML = modelField.options[modelField.selectedIndex].text
            })

            if (fast_kw && fast_time && parseFloat(fast_time) > 1 && parseFloat(fast_kw) > 1) {
              document.getElementById('speed-fast-label').style.display = 'block';
              document.getElementById('speed-fast').setAttribute('value', fast_kw);
              document.querySelector('#speed-fast-label > span').innerText = fast_kw;
            } else {
              document.getElementById('speed-fast-label').style.display = 'none';
            }
            if (rapid_kw && rapid_time && parseFloat(rapid_time) > 1 && parseFloat(rapid_kw) > 1) {
              document.getElementById('speed-rapid-label').style.display = 'block';
              document.getElementById('speed-rapid').setAttribute('value', rapid_kw);
              document.querySelector('#speed-rapid-label > span').innerText = rapid_kw;
            } else {
              document.getElementById('speed-rapid-label').style.display = 'none';
            }
          } else {
            document.getElementById('no-data').classList.remove('visuallyhidden');
          }
        }, 1000);

        let compareButton = document.querySelector('button[data-compare]');
        if (compareButton) {
          compareButton.dataset.compare = data.car.id;
          compareButton.dataset.name = data.car.model_name;
          compareButton.setAttribute('title', `Compare ${data.car.model_name} deal`);
          compareButton.innerHTML = `<svg width="11.8" height="11.8"><use xlink:href="${$base_path}/themes/bounce/img/plus.svg#plus"></use></svg>&nbsp;Compare`;
          compareButton.dataset.compareAdded = 'false';
          let cookie = Cookies.get('compare_list');
          if (cookie) {
            let cookie_obj = JSON.parse(cookie);
            if (cookie_obj[compareButton.dataset.compare] !== undefined) {
              compareButton.innerHTML = `<svg width="9.8" height="9.8"><use xlink:href="${$base_path}/themes/bounce/img/minus.svg#minus"></use></svg>&nbsp;Remove`;
              compareButton.dataset.compareAdded = 'true';
            }
          }
        }

      }).catch(function(err) {
        console.error('Fetch Error -', err);
      });
    }

    makeField.addEventListener('change', () => {
      (makeField.value > 0) ? loadModelsCalc('car', makeField.value) : false;
      (modelField.value > 0) ? stepOneNext.classList.remove('disabled') : stepOneNext.classList.add('disabled')
      derivativeField.classList.add('visuallyhidden')

      document.getElementById('no-data').classList.add('visuallyhidden')
      stepOneNext.classList.add('disabled')
    })

    modelField.addEventListener('change', () => {
      (modelField.value > 0) ? loadDerivativeCalc('car', modelField.value, false, true, true, false) : false; // inactive reversed

      derivativeField.classList.remove('visuallyhidden')

      document.getElementById('no-data').classList.add('visuallyhidden')
      stepOneNext.classList.add('disabled')

      setTimeout(() => { (derivativeField.value > 0) ? loadVehicle(derivativeField.value) : false }, 500);
    })

    derivativeField.addEventListener('change', () => {
      stepOneNext.classList.add('disabled');
      document.getElementById('no-data').classList.add('visuallyhidden');
      (derivativeField.value > 0) ? loadVehicle(derivativeField.value) : false;
    })

    electricityField.addEventListener('input', () => {
      if(electricityField.value > 0 && electricityField.value <= electricityField.getAttribute('max')){
        document.querySelector('#step-three-next').classList.remove('disabled')
      } else {
        document.querySelector('#step-three-next').classList.add('disabled')
      }
    })

    document.querySelector('#electricity-cost-button-auto').addEventListener('click', (e) => {
      // document.querySelector('#electricity-cost-button-auto').classList.remove('button-outline');
      // document.querySelector('#electricity-cost-button-manual').classList.add('button-outline');
      // document.querySelector('#electricity-cost-average').classList.remove('visuallyhidden');
      (charger_rate == '350') ? averageValue = document.querySelector('#average-data').getAttribute('data-average-electricity-ultra') : ( (charger_rate == '60') ? averageValue = document.querySelector('#average-data').getAttribute('data-average-electricity-rapid') : averageValue = document.querySelector('#average-data').getAttribute('data-average-electricity') );
      // document.querySelector('#electricity-cost-average span').innerHTML = averageValue;
      document.querySelector('#electricity-cost').setAttribute('data-average', averageValue);
      // electricityField.classList.add('visuallyhidden')
      // electricityLabel.classList.add('visuallyhidden')
      electricityField.value = averageValue
      electricityField.dispatchEvent(new Event('input'))
    })

    // document.querySelector('#electricity-cost-button-manual').addEventListener('click', (e) => {
    //   document.querySelector('#electricity-cost-button-auto').classList.add('button-outline')
    //   document.querySelector('#electricity-cost-button-manual').classList.remove('button-outline')
    //   document.querySelector('#electricity-cost-average').classList.add('visuallyhidden')
    //   electricityField.classList.remove('visuallyhidden')
    //   electricityLabel.classList.remove('visuallyhidden')
    //   electricityField.dispatchEvent(new Event('input'))
    // })

    // Calculate
    function calculate($percentage, $charger_rate, $electricity_cost, $electric_range, $battery_capacity, $charging_time) {
      function percentage(num, per){ return (num/100)*per }
      let indicative_cost = indicativeCost('Electric', null, $electric_range, $battery_capacity, $electricity_cost);
      let charge_cost = (parseFloat(indicative_cost) * parseFloat(percentage($electric_range, $percentage)))/100;
      document.getElementById('summary-miles').innerText = parseFloat(percentage($electric_range, $percentage)).toFixed(1) + ' miles';
      document.getElementById('summary-estimate').innerText = parseFloat(indicative_cost).toFixed(1) + 'p';
      document.getElementById('summary-cost').innerText = '£' + charge_cost.toFixed(2);
      document.getElementById('summary-time').innerText = (parseFloat(percentage($charging_time, $percentage))/60 < 1) ? parseFloat(percentage($charging_time, $percentage)).toFixed(0) + 'min' : (parseFloat(percentage($charging_time, $percentage))/60).toFixed(1) + 'h';
      document.getElementById('summary-kwh').innerText = parseFloat(percentage($battery_capacity, $percentage)).toFixed(0) + 'kWh';
    }

    rateSwitch.forEach((value) => {
      value.addEventListener('change', (e) => {
        charger_rate = e.target.value;
        (e.target.dataset.speed === 'fast') ? charging_time = fast_time : ((e.target.dataset.speed === 'slow') ? charging_time = slow_time : charging_time = rapid_time);
        document.querySelector('#step-two-next').classList.remove('disabled');
      })
    })

    document.querySelector('#step-three-next').addEventListener('click', (e) => {
      calculate(slider.value, charger_rate, electricityField.value, electric_range, battery_capacity, charging_time)
    })

    output.innerHTML = slider.value;
    slider.oninput = function() {
      output.innerHTML = this.value
      calculate(this.value, charger_rate, electricityField.value, electric_range, battery_capacity, charging_time)
    }

    // Reset Form
    function resetForm() {
      let compareButton = document.querySelector('button[data-compare]');
      if (compareButton) {
        compareButton.dataset.compare = '';
        compareButton.dataset.name = '';
        compareButton.setAttribute('title', `Compare deal`);
        compareButton.innerHTML = `<svg width="11.8" height="11.8"><use xlink:href="${$base_path}/themes/bounce/img/plus.svg#plus"></use></svg>&nbsp;Compare`;
        compareButton.dataset.compareAdded = 'false';
      }

      document.querySelector('#progress').value = 1;
      document.querySelector('#progress-text').innerHTML = '0';
      output.innerHTML = '80';

      document.getElementById('car-image').setAttribute('src', $base_path+'/themes/bounce/img/placeholder.png');
      document.getElementById("charging-calculator-form").reset();
      derivativeField.classList.add('visuallyhidden');
      document.querySelectorAll('.charging-calculator-form > .step').forEach((value) => {
        value.classList.remove('active')
        value.classList.contains('step-one') ? value.classList.add('active') : false;
      })
      document.querySelectorAll('.next-step').forEach((value) => { value.classList.add('disabled') })

      // document.querySelector('#electricity-cost-button-auto').classList.add('button-outline')
      // document.querySelector('#electricity-cost-button-manual').classList.remove('button-outline')
      // document.querySelector('#electricity-cost-average').classList.add('visuallyhidden')
      // electricityField.classList.remove('visuallyhidden')
      // electricityLabel.classList.remove('visuallyhidden')
    }
    setTimeout(resetForm, 500);
    document.querySelectorAll('.start-again').forEach((value) => {
      value.addEventListener('click', resetForm);
    })
  }

  // Tax Calculator
  if (document.querySelector('body').classList.contains('page-tax-calculator')) {
    loadManufacturersCalc();
    let makeField = document.querySelector('select[name=calculator-make]')
    let modelField = document.querySelector('select[name=calculator-model]')
    let derivativeField = document.querySelector('select[name=calculator-derivative]')
    let makeFieldCompare = document.querySelector('select[name=calculator-make-compare]')
    let modelFieldCompare = document.querySelector('select[name=calculator-model-compare]')
    let derivativeFieldCompare = document.querySelector('select[name=calculator-derivative-compare]')
    let stepOneNext = document.querySelector('#step-one-next')
    let stepTwoNext = document.querySelector('#step-two-next')
    let tax_band = 20;
    let ev_loaded, ice_loaded = false;
    let fuel_type, img_url, p11d, bik1, bik2, bik3, indicative_fuel_cost, co2;
    let comparable_value, comparable_type, comparable_img_url, comparable_mpg, comparable_p11d, comparable_bik1, comparable_bik2, comparable_bik3, comparable_indicative_fuel_cost, comparable_co2;

    function loadVehicle($derivative, $ev = false) {
      let url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/car/' + $derivative;
      fetch(url).then(function(response) {
        if (response.status !== 200) {
          console.warn('Looks like there was a problem. Status Code: ' + response.status);
          return;
        }
        return response.json();
      }).then(function(data) {
        // console.log(data)
        if ($ev) {
          co2 = (data.car.co2) ? data.car.co2 : null;
          bik1 = (data.car.tax) ? data.car.tax.Year1 : null;
          bik2 = (data.car.tax) ? data.car.tax.Year2 : null;
          bik3 = (data.car.tax) ? data.car.tax.Year3 : null;
          p11d = (data.car.base_price) ? data.car.base_price : null;
          img_url = (data.car.main_img) ? data.car.main_img : null;
          fuel_type = (data.car.fuel_type) ? data.car.fuel_type : null;
          // console.log(co2, bik1, bik2, bik3, p11d, img_url, fuel_type);
          // ved_first = data.car.tax_band.rate;
          // ved_annual = data.car.tax_band.second_rate;
          indicative_fuel_cost = indicativeCost(fuel_type, null, (data.car.electric_range != null) ? data.car.electric_range : null, data.car.battery_capacity);
          setTimeout(() => {
            if (co2 && img_url && fuel_type && bik1 != null && data.car.electric_range && data.car.battery_capacity) {
              ev_loaded = true;
              document.getElementById('no-data').classList.add('visuallyhidden');
              (derivativeField.childElementCount > 0 && derivativeFieldCompare.childElementCount > 0 && ice_loaded) ? stepOneNext.classList.remove('disabled') : stepOneNext.classList.add('disabled');
            } else {
              ev_loaded = false;
              document.getElementById('no-data').classList.remove('visuallyhidden');
            }
          }, 1000);

          let compareButton = document.querySelector('button[data-compare]');
          if (compareButton) {
            compareButton.dataset.compare = data.car.id;
            compareButton.dataset.name = data.car.model_name;
            compareButton.setAttribute('title', `Compare ${data.car.model_name} deal`);
            compareButton.innerHTML = `<svg width="11.8" height="11.8"><use xlink:href="${$base_path}/themes/bounce/img/plus.svg#plus"></use></svg>&nbsp;Compare`;
            compareButton.dataset.compareAdded = 'false';
            let cookie = Cookies.get('compare_list');
            if (cookie) {
              let cookie_obj = JSON.parse(cookie);
              if (cookie_obj[compareButton.dataset.compare] !== undefined) {
                compareButton.innerHTML = `<svg width="9.8" height="9.8"><use xlink:href="${$base_path}/themes/bounce/img/minus.svg#minus"></use></svg>&nbsp;Remove`;
                compareButton.dataset.compareAdded = 'true';
              }
            }
          }
        } else {
          comparable_type = data.car.fuel_type;
          if (comparable_type === 'Electric') {
            setTimeout(() => {
              ice_loaded = false;
              stepOneNext.classList.add('disabled');
              document.getElementById('no-ice-two').classList.remove('visuallyhidden');
            }, 500);
          } else {
            comparable_co2 = data.car.co2;
            comparable_img_url = data.car.main_img;
            comparable_p11d = data.car.base_price;
            // comparable_ved_first = data.car.tax_band.rate;
            // comparable_ved_annual = data.car.tax_band.second_rate;
            comparable_value = data.car.base_price;
            comparable_bik1 = data.car.tax.Year1;
            comparable_bik2 = data.car.tax.Year2;
            comparable_bik3 = data.car.tax.Year3;
            comparable_mpg = (data.car.mpg_combined != null) ? data.car.mpg_combined : ((data.car.mpg_urban != null) ? data.car.mpg_urban : data.car.mpg_extra);
            comparable_indicative_fuel_cost = indicativeCost(comparable_type, comparable_mpg, null, null);
            setTimeout(() => {
              if (comparable_value && comparable_bik1 != null && comparable_mpg && comparable_co2) {
                ice_loaded = true;
                document.getElementById('no-data-two').classList.add('visuallyhidden');
                document.getElementById('no-ice-two').classList.add('visuallyhidden');
                (derivativeField.childElementCount > 0 && derivativeFieldCompare.childElementCount > 0 && ev_loaded) ? stepOneNext.classList.remove('disabled') : stepOneNext.classList.add('disabled');
              } else {
                ice_loaded = false;
                document.getElementById('no-data-two').classList.remove('visuallyhidden');
              }
            }, 1000);
          }
        }
      }).catch(function(err) {
        console.error('Fetch Error -', err);
      });
    }

    // First Step
    makeField.addEventListener('change', () => {
      (makeField.value > 0) ? loadModelsCalc('car', makeField.value) : null;
      (modelField.value > 0) ? stepOneNext.classList.remove('disabled') : stepOneNext.classList.add('disabled')
      Array.prototype.slice.call(document.querySelectorAll('.step-three-make'), 0).forEach((value) => {
        value.innerHTML = makeField.options[makeField.selectedIndex].text
      })
      derivativeField.classList.add('visuallyhidden')
      let buttonUrl = $base_path+'/cars/' + makeField.options[makeField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase()
      document.querySelector('.view-range-button').setAttribute('href', buttonUrl)
    })
    modelField.addEventListener('change', () => {
      (modelField.value > 0) ? loadDerivativeCalc('car', modelField.value, false, true, true, false) : null; // inactive reversed
      Array.prototype.slice.call(document.querySelectorAll('.step-three-model'), 0).forEach((value) => {
        value.innerHTML = modelField.options[modelField.selectedIndex].text
      })
      let buttonUrl = $base_path+'/cars/' + makeField.options[makeField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase() + '/' + modelField.options[modelField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase()
      document.querySelector('.view-range-button').setAttribute('href', buttonUrl)
      derivativeField.classList.remove('visuallyhidden')

      document.getElementById('no-data').classList.add('visuallyhidden')
      stepOneNext.classList.add('disabled')

      setTimeout(() => { (derivativeField.value > 0) ? loadVehicle(derivativeField.value, true) : null }, 500);
    })
    derivativeField.addEventListener('change', () => {
      stepOneNext.classList.add('disabled');
      document.getElementById('no-data').classList.add('visuallyhidden');
      (derivativeField.value > 0) ? loadVehicle(derivativeField.value, true) : null;
    })

    // Second Step
    makeFieldCompare.addEventListener('change', () => {
      (makeFieldCompare.value > 0) ? loadModelsCalc('car', makeFieldCompare.value, false, true, true) : null;
      (modelFieldCompare.value > 0) ? stepOneNext.classList.remove('disabled') : stepOneNext.classList.add('disabled')
      derivativeFieldCompare.classList.add('visuallyhidden')
      document.querySelector('.step-four-make-second').innerHTML = makeFieldCompare.options[makeFieldCompare.selectedIndex].text;
      document.getElementById('no-data-two').classList.add('visuallyhidden')
      stepOneNext.classList.add('disabled')
    })
    modelFieldCompare.addEventListener('change', () => {
      (modelFieldCompare.value > 0) ? loadDerivativeCalc('car', modelFieldCompare.value, false, false, true, true) : null;
      document.querySelector('.step-four-model-second').innerHTML = modelFieldCompare.options[modelFieldCompare.selectedIndex].text;
      derivativeFieldCompare.classList.remove('visuallyhidden')

      document.getElementById('no-data-two').classList.add('visuallyhidden')
      stepOneNext.classList.add('disabled')

      setTimeout(() => { (derivativeFieldCompare.value > 0) ? loadVehicle(derivativeFieldCompare.value) : null }, 500);
    })
    derivativeFieldCompare.addEventListener('change', () => {
      stepOneNext.classList.add('disabled');
      document.getElementById('no-data-two').classList.add('visuallyhidden');
      (derivativeFieldCompare.value > 0) ? loadVehicle(derivativeFieldCompare.value) : null;
    })

    // Third Step - Company Switch
    // document.querySelectorAll('input[name=company]').forEach((value) => {
    //   value.addEventListener('change', (e) => {
    //     (e.target.value === 'yes') ? company_car = true : company_car = false;
    //     (e.target.value === 'yes') ? tax_band = 20 : tax_band = 0;
    //     (e.target.value === 'yes') ? taxBand.classList.remove('visuallyhidden') : taxBand.classList.add('visuallyhidden');
    //     (e.target.value === 'yes') ? stepThreeNext.classList.add('disabled') : stepThreeNext.classList.remove('disabled')
    //   })
    // })

    // Third Step - Tax Band Switch
    document.querySelector('select[name=calculator-tax-band]').addEventListener('change', (e) => {
      // (e.target.value > 0) ? stepTwoNext.classList.remove('disabled') : stepTwoNext.classList.add('disabled');
      (e.target.value === '20') ? tax_band = 20 : tax_band = 40;
      document.querySelectorAll('.tax-payable').forEach((value => {value.innerHTML = e.target.value}))
    })

    // Fourth Step - Frequency Switch
    // document.querySelectorAll('input[name=frequency]').forEach((value) => {
    //   value.addEventListener('change', (e) => {
    //     congestion_charge_freq = e.target.value
    //   })
    // })

    function calculateBIK($p11d, $bik_rate) {
      return ($p11d * ($bik_rate / 100));
    }
    function calculateTAX($p11d, $bik_rate, $tax_rate = 0) {
      return ($p11d * ($bik_rate / 100)) * ($tax_rate / 100);
    }
    // function calculateNIC($p11d, $bik_rate, $nic_rate = 13.80) {
    //   return ($p11d * ($bik_rate / 100)) * ($nic_rate / 100);
    // }

    // Calculate
    stepTwoNext.addEventListener('click', (e) => {
      // let ved_total = (ved_first + (2 * ved_annual));
      let bik_total = (calculateBIK(p11d, bik1, tax_band) + calculateBIK(p11d, bik2, tax_band) + calculateBIK(p11d, bik3, tax_band)).toFixed(0);
      let tax_total = (calculateTAX(p11d, bik1, tax_band) + calculateTAX(p11d, bik2, tax_band) + calculateTAX(p11d, bik3, tax_band)).toFixed(0);
      document.getElementById('car-image').setAttribute('src', img_url)
      document.getElementById('summary-type').innerText = fuel_type
      // document.getElementById('summary-ved-first').innerText = '£' + ved_first
      // document.getElementById('summary-ved-annual').innerText = '£' + ved_annual
      // document.getElementById('summary-ved-total').innerText = '£' + ved_total
      document.getElementById('summary-p11d').innerText = '£' + (p11d)
      document.getElementById('summary-co2').innerText = (co2) + 'g/km'
      document.getElementById('summary-bik1').innerText = '£' + (calculateBIK(p11d, bik1)).toFixed(0)
      document.getElementById('summary-bik2').innerText = '£' + (calculateBIK(p11d, bik2)).toFixed(0)
      document.getElementById('summary-bik3').innerText = '£' + (calculateBIK(p11d, bik3)).toFixed(0)
      document.getElementById('summary-bik1tax').innerText = '£' + (calculateTAX(p11d, bik1, tax_band)).toFixed(0)
      document.getElementById('summary-bik2tax').innerText = '£' + (calculateTAX(p11d, bik2, tax_band)).toFixed(0)
      document.getElementById('summary-bik3tax').innerText = '£' + (calculateTAX(p11d, bik3, tax_band)).toFixed(0)
      document.getElementById('summary-bik1rate').innerText = (bik1) + '%'
      document.getElementById('summary-bik2rate').innerText = (bik2) + '%'
      document.getElementById('summary-bik3rate').innerText = (bik3) + '%'
      // document.getElementById('summary-bik4').innerText = '£' + bik_total
      document.getElementById('summary-fuel').innerText = (indicative_fuel_cost) + 'p/mile'

      // let comparable_ved_total = (comparable_ved_first + (2 * comparable_ved_annual));
      let comparable_bik_total = (calculateBIK(comparable_p11d, comparable_bik1, tax_band) + calculateBIK(comparable_p11d, comparable_bik2, tax_band) + calculateBIK(comparable_p11d, comparable_bik3, tax_band)).toFixed(0);
      let comparable_tax_total = (calculateTAX(comparable_p11d, comparable_bik1, tax_band) + calculateTAX(comparable_p11d, comparable_bik2, tax_band) + calculateTAX(comparable_p11d, comparable_bik3, tax_band)).toFixed(0);
      // document.getElementById('car-image-second').setAttribute('src', comparable_img_url)
      document.getElementById('summary-type-second').innerText = comparable_type
      // document.getElementById('summary-ved-first-second').innerText = '£' + comparable_ved_first
      // document.getElementById('summary-ved-annual-second').innerText = '£' + comparable_ved_annual
      // document.getElementById('summary-ved-total-second').innerText = '£' + comparable_ved_total
      document.getElementById('summary-p11d-second').innerText = '£' + (comparable_p11d)
      document.getElementById('summary-co2-second').innerText = (comparable_co2) + 'g/km'
      document.getElementById('summary-bik1-second').innerText = '£' + (calculateBIK(comparable_p11d, comparable_bik1)).toFixed(0)
      document.getElementById('summary-bik2-second').innerText = '£' + (calculateBIK(comparable_p11d, comparable_bik2)).toFixed(0)
      document.getElementById('summary-bik3-second').innerText = '£' + (calculateBIK(comparable_p11d, comparable_bik3)).toFixed(0)
      document.getElementById('summary-bik1tax-second').innerText = '£' + (calculateTAX(comparable_p11d, comparable_bik1, tax_band)).toFixed(0)
      document.getElementById('summary-bik2tax-second').innerText = '£' + (calculateTAX(comparable_p11d, comparable_bik2, tax_band)).toFixed(0)
      document.getElementById('summary-bik3tax-second').innerText = '£' + (calculateTAX(comparable_p11d, comparable_bik3, tax_band)).toFixed(0)
      document.getElementById('summary-bik1rate-second').innerText = (comparable_bik1) + '%'
      document.getElementById('summary-bik2rate-second').innerText = (comparable_bik2) + '%'
      document.getElementById('summary-bik3rate-second').innerText = (comparable_bik3) + '%'
      // document.getElementById('summary-bik4-second').innerText = '£' + comparable_bik_total
      document.getElementById('summary-fuel-second').innerText = (comparable_indicative_fuel_cost) + 'p/mile'

      document.getElementById('summary-total-tax').innerText = '£' + (tax_total);
      document.getElementById('summary-total-tax-second').innerText = '£' + (comparable_tax_total);
      document.getElementById('summary-total-savings').innerText = '£' + ((parseFloat(comparable_tax_total)) - (parseFloat(tax_total)));

      // let comparable_tax_amount;
      // let comparable_bik_rate = (comparable_bik / 100) * comparable_value;
      // (tax_band === 0) ? comparable_tax_amount = comparable_bik_rate : comparable_tax_amount = (tax_band / 100) * comparable_bik_rate;
      // document.getElementById('summary-total-tax').innerText = '£' + (ved_total + ((fuel_type === 'Electric') ? 0 : ((congestion_charge * congestion_charge_freq) * 3)))
      // document.getElementById('summary-total-savings').innerText = '£' + ((fuel_type !== 'Electric') ? 0 : (((parseFloat(congestion_charge) * parseFloat(congestion_charge_freq)) + parseFloat(comparable_tax_amount)) * 3)).toFixed(0)
    })

    // Reset Form
    function resetForm() {
      let compareButton = document.querySelector('button[data-compare]');
      if (compareButton) {
        compareButton.dataset.compare = '';
        compareButton.dataset.name = '';
        compareButton.setAttribute('title', `Compare deal`);
        compareButton.innerHTML = `<svg width="11.8" height="11.8"><use xlink:href="${$base_path}/themes/bounce/img/plus.svg#plus"></use></svg>&nbsp;Compare`;
        compareButton.dataset.compareAdded = 'false';
      }

      document.querySelector('#progress').value = 1;
      document.querySelector('#progress-text').innerHTML = '0';

      document.getElementById('car-image').setAttribute('src', $base_path+'/themes/bounce/img/placeholder.png');
      stepOneNext.classList.add('disabled')
      derivativeField.classList.add('visuallyhidden')
      derivativeFieldCompare.classList.add('visuallyhidden')
      document.getElementById("tax-calculator-form").reset()
      document.querySelectorAll('.tax-calculator-form > .step').forEach((value) => {
        value.classList.remove('active')
        value.classList.contains('step-one') ? value.classList.add('active') : null
      })
      document.querySelector('#step-one-next').classList.add('disabled')
    }
    setTimeout(resetForm, 500);
    document.querySelectorAll('.start-again').forEach((value) => {
      value.addEventListener('click', resetForm);
    })

  }

  // Charge Point Map
  if (document.querySelector('body').classList.contains('page-charging-points-finder')) {
    document.querySelectorAll('#map-filter-buttons input').forEach((value) => {
      value.addEventListener('change', (v) => {
        if (v.target.value === 'all'){
          document.getElementById('oc-map').src = document.getElementById('oc-map').dataset.src + '&operatorid=';
        } else {
          document.getElementById('oc-map').src = document.getElementById('oc-map').dataset.src + '&operatorid=' + v.target.value;
        }
      })
    })
  }

  // Compare Vehicles
  if (document.querySelector('body').classList.contains('page-compare-vehicles')) {
    loadManufacturersCalc('car', true);
    let comparisonTable = document.getElementById('car-comparison')
    let makeField = document.querySelector('select[name=calculator-make]')
    let modelField = document.querySelector('select[name=calculator-model]')
    let derivativeField = document.querySelector('select[name=calculator-derivative]')

    function compareColumns(row, reverse = false, blue = false) {
      let largest = 0
      let smallest = 9999
      comparisonTable.querySelectorAll(`[data-cell-content="${row}"] [data-car][data-compare]`).forEach((value, key, parent) => {
        if (comparisonTable.querySelectorAll(`[data-cell-content="${row}"] [data-car][data-compare]`).length > 1) {
          (largest < parseInt(value.getAttribute('data-compare'))) ? largest = value.getAttribute('data-compare') : '';
          (smallest > parseInt(value.getAttribute('data-compare'))) ? smallest = value.getAttribute('data-compare'): '';
          comparisonTable.querySelectorAll(`[data-cell-content="${row}"] [data-car][data-compare]`).forEach((v) => {v.setAttribute('data-highlight', '')});
          if(largest !== smallest) {
            comparisonTable.querySelectorAll(`[data-cell-content="${row}"] [data-car][data-compare='${largest}']`).forEach((v) => {v.setAttribute('data-highlight', `up-${(blue) ? 'blue' : ((reverse) ? 'red' : 'green')}`)})
            comparisonTable.querySelectorAll(`[data-cell-content="${row}"] [data-car][data-compare='${smallest}']`).forEach((v) => {v.setAttribute('data-highlight', `down-${(blue) ? 'blue' : ((reverse) ? 'green' : 'red')}`)})
          }
        }
      })
    }

    function loadPrice($spot, $derivative) {
      comparisonTable.querySelector(`[data-cell-content="initial-profile"] [data-car=first] span`).innerText = document.querySelector('.quote-terms[name=profile]').options[document.querySelector('.quote-terms[name=profile]').selectedIndex].text;
      comparisonTable.querySelector(`[data-cell-content="initial-profile"] [data-car=second] span`).innerText = document.querySelector('.quote-terms[name=profile]').options[document.querySelector('.quote-terms[name=profile]').selectedIndex].text;
      comparisonTable.querySelector(`[data-cell-content="initial-profile"] [data-car=third] span`).innerText = document.querySelector('.quote-terms[name=profile]').options[document.querySelector('.quote-terms[name=profile]').selectedIndex].text;
      comparisonTable.querySelector(`[data-cell-content="contract-length"] [data-car=first] span`).innerText = document.querySelector('.quote-terms[name=term]').options[document.querySelector('.quote-terms[name=term]').selectedIndex].text;
      comparisonTable.querySelector(`[data-cell-content="contract-length"] [data-car=second] span`).innerText = document.querySelector('.quote-terms[name=term]').options[document.querySelector('.quote-terms[name=term]').selectedIndex].text;
      comparisonTable.querySelector(`[data-cell-content="contract-length"] [data-car=third] span`).innerText = document.querySelector('.quote-terms[name=term]').options[document.querySelector('.quote-terms[name=term]').selectedIndex].text;
      comparisonTable.querySelector(`[data-cell-content="annual-mileage"] [data-car=first] span`).innerText = document.querySelector('.quote-terms[name=mileage]').options[document.querySelector('.quote-terms[name=mileage]').selectedIndex].text;
      comparisonTable.querySelector(`[data-cell-content="annual-mileage"] [data-car=second] span`).innerText = document.querySelector('.quote-terms[name=mileage]').options[document.querySelector('.quote-terms[name=mileage]').selectedIndex].text;
      comparisonTable.querySelector(`[data-cell-content="annual-mileage"] [data-car=third] span`).innerText = document.querySelector('.quote-terms[name=mileage]').options[document.querySelector('.quote-terms[name=mileage]').selectedIndex].text;
      comparisonTable.querySelector(`[data-cell-content="price"] [data-car=${$spot}] span`).innerText = 'Loading...';

      document.querySelector('input[name="car_id"]').value = $derivative;
      let url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/car/price/' + $derivative;
      let formData = new FormData(document.getElementById('car-comparison'));
      fetch(url, {
        method: 'POST',
        body: formData,
      }).then(function (response) {
        if (response.status !== 200) {
          console.warn('Looks like there was a problem. Status Code: ' + response.status);
          return;
        }
        return response.json();
      }).then(function (data) {
        if (data) {
          comparisonTable.querySelector(`[data-cell-content="price"] [data-car=${$spot}] span`).innerText = (data.monthly_rental != null) ? '£' + data.monthly_rental : 'N/A';
          (parseInt(data.monthly_rental) >= 0) ? comparisonTable.querySelector(`[data-cell-content="price"] [data-car=${$spot}]`).setAttribute('data-compare', data.monthly_rental) : '';
          compareColumns('price', true);
          // comparisonTable.querySelector(`[data-cell-content="contract-length"] [data-car=${$spot}] span`).innerText = (data.term != null) ? data.term + ' months' : 'N/A';
          // (parseInt(data.term) >= 0) ? comparisonTable.querySelector(`[data-cell-content="contract-length"] [data-car=${$spot}]`).setAttribute('data-compare', data.term) : '';
          // comparisonTable.querySelector(`[data-cell-content="initial-profile"] [data-car=${$spot}] span`).innerText = (data.initial_profile != null) ? data.initial_profile + ' months' : 'N/A';
          // (parseInt(data.initial_profile) >= 0) ? comparisonTable.querySelector(`[data-cell-content="initial-profile"] [data-car=${$spot}]`).setAttribute('data-compare', data.initial_profile) : '';
          // comparisonTable.querySelector(`[data-cell-content="annual-mileage"] [data-car=${$spot}] span`).innerText = (data.mileage != null) ? data.mileage + ' miles' : 'N/A';
          // (parseInt(data.mileage) >= 0) ? comparisonTable.querySelector(`[data-cell-content="annual-mileage"] [data-car=${$spot}]`).setAttribute('data-compare', data.mileage) : '';
        }
      }).catch(function (err) {
        console.error('Fetch Error -', err);
        document.getElementsByClassName('price')[0].classList.remove('hidden');
        document.getElementsByClassName('price-fetching')[0].classList.add('hidden');
      });
    }

    function setLinks($spot, $active, $url, $fuel) {
      let params = '?' + new URLSearchParams({
        'term': document.querySelector('.quote-terms[name=term]').options[document.querySelector('.quote-terms[name=term]').selectedIndex].value,
        'profile': document.querySelector('.quote-terms[name=profile]').options[document.querySelector('.quote-terms[name=profile]').selectedIndex].value,
        'mileage': document.querySelector('.quote-terms[name=mileage]').options[document.querySelector('.quote-terms[name=mileage]').selectedIndex].value
      });
      ($active == '1' && $fuel == 'Electric') ? comparisonTable.querySelector(`[data-cell-content="cta_top"] [data-car=${$spot}] a`).setAttribute('href', (typeof $base_path !== 'undefined' ? $base_path : '') + $url + params) : comparisonTable.querySelector(`[data-cell-content="cta_top"] [data-car=${$spot}] a`).classList.add('visuallyhidden');
      ($active == '1' && $fuel == 'Electric') ? comparisonTable.querySelector(`[data-cell-content="model"] [data-car=${$spot}] a`).setAttribute('href', (typeof $base_path !== 'undefined' ? $base_path : '') + $url + params) : comparisonTable.querySelector(`[data-cell-content="model"] [data-car=${$spot}] a`).classList.add('visuallyhidden');
      ($active == '1' && $fuel == 'Electric') ? comparisonTable.querySelector(`[data-cell-content="cta"] [data-car=${$spot}] a`).setAttribute('href', (typeof $base_path !== 'undefined' ? $base_path : '') + $url + params) : comparisonTable.querySelector(`[data-cell-content="cta"] [data-car=${$spot}] a`).classList.add('visuallyhidden');
    }

    function loadVehicle($spot, $derivative) {
      if ($derivative !== 0) {
        let url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/car/' + $derivative;
        let params = '?' + new URLSearchParams({'type': (document.querySelector('#car-comparison input[name=type]')).value});
        fetch(url + params).then(function(response) {
          if (response.status !== 200) {
            console.warn('Looks like there was a problem. Status Code: ' + response.status);
            return;
          }
          return response.json();
        }).then(function(data) {
          let getCapTechnicalData;
          if(data.car.technical) {
            getCapTechnicalData = ($category, $term) => (data.car.technical[$category] !== undefined) ? (data.car.technical[$category].find(item => item.name.includes($term))) : false;
          } else {
            getCapTechnicalData = ($category, $term) => 'N/A';
          }
          comparisonTable.setAttribute(`data-car-${$spot}`, $derivative);

          comparisonTable.querySelector(`[data-cell-content="model"] [data-car=${$spot}] .make`).innerText = data.car.range_name;
          comparisonTable.querySelector(`[data-cell-content="model"] [data-car=${$spot}] .derivative`).innerText = data.car.name;

          (data.car.active == '1' && data.car.fuel_type == 'Electric') ? comparisonTable.querySelector(`[data-cell-content="cta_top"] [data-car=${$spot}] a`).setAttribute('href', (typeof $base_path !== 'undefined' ? $base_path : '') + data.car.full_url) : comparisonTable.querySelector(`[data-cell-content="cta_top"] [data-car=${$spot}] a`).classList.add('visuallyhidden');
          (data.car.active == '1' && data.car.fuel_type == 'Electric') ? comparisonTable.querySelector(`[data-cell-content="model"] [data-car=${$spot}] a`).setAttribute('href', (typeof $base_path !== 'undefined' ? $base_path : '') + data.car.full_url) : comparisonTable.querySelector(`[data-cell-content="model"] [data-car=${$spot}] a`).classList.add('visuallyhidden');

          comparisonTable.querySelector(`[data-cell-content="img"] [data-car=${$spot}] img`).setAttribute('src', data.car.main_img);

          setLinks($spot, data.car.active, data.car.full_url, data.car.fuel_type);

          comparisonTable.querySelector(`[data-cell-content="fuel-type"] [data-car=${$spot}] span`).innerText = (data.car.fuel_type) ? data.car.fuel_type : 'N/A';
          comparisonTable.querySelector(`[data-cell-content="p11d"] [data-car=${$spot}] span`).innerText = (data.car.base_price) ? '£' + (parseFloat(data.car.base_price)).toFixed(0) : 'N/A';
          comparisonTable.querySelector(`[data-cell-content="electric-range"] [data-car=${$spot}] span`).innerText = (getCapTechnicalData('Electric Vehicle Data', 'Pure Electric Range (miles) - Comb - Min') && getCapTechnicalData('Electric Vehicle Data', 'Pure Electric Range (miles) - Comb - Min').value) ? getCapTechnicalData('Electric Vehicle Data', 'Pure Electric Range (miles) - Comb - Min').value + ' miles' : ((data.car.electric_range) ? data.car.electric_range + ' miles' : 'N/A');
          (getCapTechnicalData('Electric Vehicle Data', 'Pure Electric Range (miles) - Comb - Min') && getCapTechnicalData('Electric Vehicle Data', 'Pure Electric Range (miles) - Comb - Min').value) ? comparisonTable.querySelector(`[data-cell-content="electric-range"] [data-car=${$spot}]`).setAttribute('data-compare', getCapTechnicalData('Electric Vehicle Data', 'Pure Electric Range (miles) - Comb - Min').value) : comparisonTable.querySelector(`[data-cell-content="electric-range"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.electric_range);
          compareColumns('electric-range');
          comparisonTable.querySelector(`[data-cell-content="real-electric-range"] [data-car=${$spot}] span`).innerText = (data.car.electric_range_real_world) ? data.car.electric_range_real_world + ' miles' : 'N/A';
          (parseInt(data.car.electric_range_real_world) > 0) ? comparisonTable.querySelector(`[data-cell-content="real-electric-range"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.electric_range_real_world) : '';
          compareColumns('real-electric-range');

          comparisonTable.querySelector(`[data-cell-content="official-mpg"] [data-car=${$spot}] span`).innerText = (data.car.mpg_combined && parseInt(data.car.mpg_combined) > 0) ? data.car.mpg_combined + ' mpg' : 'N/A';
          (parseInt(data.car.mpg_combined) > 0) ? comparisonTable.querySelector(`[data-cell-content="official-mpg"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.mpg_combined) : '';
          compareColumns('official-mpg');
          comparisonTable.querySelector(`[data-cell-content="real-mpg"] [data-car=${$spot}] span`).innerText = (getCapTechnicalData('Fuel Consumption - ICE', 'WLTP - MPG - Comb - Max') && getCapTechnicalData('Fuel Consumption - ICE', 'WLTP - MPG - Comb - Max').value) ? getCapTechnicalData('Fuel Consumption - ICE', 'WLTP - MPG - Comb - Max').value + ' mpg' : 'N/A';
          (getCapTechnicalData('Fuel Consumption - ICE', 'WLTP - MPG - Comb - Max')) ? comparisonTable.querySelector(`[data-cell-content="real-mpg"] [data-car=${$spot}]`).setAttribute('data-compare', getCapTechnicalData('Fuel Consumption - ICE', 'WLTP - MPG - Comb - Max').value) : '';
          compareColumns('real-mpg');

          let indicative_fuel_cost = indicativeCost(data.car.fuel_type, (data.car.mpg_combined != null) ? data.car.mpg_combined : ((data.car.mpg_urban != null) ? data.car.mpg_urban : data.car.mpg_extra), (data.car.electric_range != null) ? data.car.electric_range : null, data.car.battery_capacity);
          comparisonTable.querySelector(`[data-cell-content="indicative-cost"] [data-car=${$spot}] span`).innerText = (parseInt(indicative_fuel_cost) >= 0) ? indicative_fuel_cost + ' p/mile' : 'N/A';
          (parseInt(indicative_fuel_cost) >= 0) ? comparisonTable.querySelector(`[data-cell-content="indicative-cost"] [data-car=${$spot}]`).setAttribute('data-compare', indicative_fuel_cost) : '';
          compareColumns('indicative-cost', true);

          // comparisonTable.querySelector(`[data-cell-content="green-car-rating"] [data-car=${$spot}] span`).innerText = 'N/A';
          // comparisonTable.querySelector(`[data-cell-content="climate-air"] [data-car=${$spot}] span`).innerText = 'N/A';getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions')
          comparisonTable.querySelector(`[data-cell-content="co2-emissions"] [data-car=${$spot}] span`).innerText = (data.car.co2) ? data.car.co2 + ' g/km' : 'N/A';
          comparisonTable.querySelector(`[data-cell-content="euro-standard"] [data-car=${$spot}] span`).innerText = (getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions') && getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions').value) ? getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions').value : 'N/A';
          // comparisonTable.querySelector(`[data-cell-content="nox"] [data-car=${$spot}] span .nox`).innerText = (getCapTechnicalData('Emissions - ICE', 'NOx')) ? getCapTechnicalData('Emissions - ICE', 'NOx').value : 'Not Available';
          // comparisonTable.querySelector(`[data-cell-content="nox"] [data-car=${$spot}] span .particles`).innerText = (getCapTechnicalData('Emissions - ICE', 'Particles')) ? getCapTechnicalData('Emissions - ICE', 'Particles').value : 'Not Available';

          if (data.car.tax_band) {
            comparisonTable.querySelector(`[data-cell-content="tax-band"] [data-car=${$spot}] span`).innerText = (data.car.tax_band.band != null) ? data.car.tax_band.band : 'N/A';
            comparisonTable.querySelector(`[data-cell-content="first-year-rate"] [data-car=${$spot}] span`).innerText = (data.car.tax_band.rate != null) ? '£' + data.car.tax_band.rate : 'N/A';
            (parseInt(data.car.tax_band.rate) >= 0) ? comparisonTable.querySelector(`[data-cell-content="first-year-rate"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.tax_band.rate) : '';
            compareColumns('first-year-rate', true);
            comparisonTable.querySelector(`[data-cell-content="year-rate"] [data-car=${$spot}] span`).innerText = (data.car.tax_band.second_rate != null) ? '£' + data.car.tax_band.second_rate : 'N/A';
            (parseInt(data.car.tax_band.second_rate) >= 0) ? comparisonTable.querySelector(`[data-cell-content="year-rate"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.tax_band.second_rate) : '';
            compareColumns('year-rate', true);
          }

          if (data.car.fuel_type === 'Petrol') {
            comparisonTable.querySelector(`[data-cell-content="congestion-charge"] [data-car=${$spot}] span`).innerText = (data.car.tax_band.london != null) ? '£' + data.car.tax_band.london : 'N/A';
            comparisonTable.querySelector(`[data-cell-content="ulez-charge"] [data-car=${$spot}] span`).innerText = (getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions')) ? ((parseInt(getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions').value.slice(-1)) >= 4) ? '£12.50' : '£0') : 'N/A';
          } else if (data.car.fuel_type === 'Diesel') {
            comparisonTable.querySelector(`[data-cell-content="congestion-charge"] [data-car=${$spot}] span`).innerText = (data.car.tax_band.london != null) ? '£' + data.car.tax_band.london : 'N/A';
            comparisonTable.querySelector(`[data-cell-content="ulez-charge"] [data-car=${$spot}] span`).innerText = (getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions')) ? ((parseInt(getCapTechnicalData('Emissions - ICE', 'Standard Euro Emissions').value.slice(-1)) >= 6) ? '£12.50' : '£0') : 'N/A';
          } else if (data.car.fuel_type === 'Electric') {
            comparisonTable.querySelector(`[data-cell-content="congestion-charge"] [data-car=${$spot}] span`).innerText = '£0';
            comparisonTable.querySelector(`[data-cell-content="ulez-charge"] [data-car=${$spot}] span`).innerText = '£0';
          } else {
            comparisonTable.querySelector(`[data-cell-content="congestion-charge"] [data-car=${$spot}] span`).innerText = (data.car.tax_band.london != null) ? '£' + data.car.tax_band.london : 'N/A';
            comparisonTable.querySelector(`[data-cell-content="ulez-charge"] [data-car=${$spot}] span`).innerText = '£0';
          }

          if (data.car.tax) {
            comparisonTable.querySelector(`[data-cell-content="bik-1"] [data-car=${$spot}] span`).innerText = (data.car.tax.Year1 != null) ? data.car.tax.Year1 + '%' : 'N/A';
            (parseInt(data.car.tax.Year1) >= 0) ? comparisonTable.querySelector(`[data-cell-content="bik-1"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.tax.Year1) : '';
            compareColumns('bik-1', true);
            comparisonTable.querySelector(`[data-cell-content="bik-2"] [data-car=${$spot}] span`).innerText = (data.car.tax.Year2 != null) ? data.car.tax.Year2 + '%' : 'N/A';
            (parseInt(data.car.tax.Year2) >= 0) ? comparisonTable.querySelector(`[data-cell-content="bik-2"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.tax.Year2) : '';
            compareColumns('bik-2', true);
            comparisonTable.querySelector(`[data-cell-content="bik-3"] [data-car=${$spot}] span`).innerText = (data.car.tax.Year3 != null) ? data.car.tax.Year3 + '%' : 'N/A';
            (parseInt(data.car.tax.Year3) >= 0) ? comparisonTable.querySelector(`[data-cell-content="bik-3"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.tax.Year3) : '';
            compareColumns('bik-3', true);
          }

          if (data.car.from_price) {
            comparisonTable.querySelector(`[data-cell-content="price"] [data-car=${$spot}] span`).innerText = (data.car.from_price.monthly_rental != null) ? '£' + data.car.from_price.monthly_rental : 'N/A';
            (parseInt(data.car.from_price.monthly_rental) >= 0) ? comparisonTable.querySelector(`[data-cell-content="price"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.from_price.monthly_rental) : '';
            compareColumns('price', true);
            comparisonTable.querySelector(`[data-cell-content="contract-length"] [data-car=${$spot}] span`).innerText = (data.car.from_price.term != null) ? data.car.from_price.term + ' months' : 'N/A';
            (parseInt(data.car.from_price.term) >= 0) ? comparisonTable.querySelector(`[data-cell-content="contract-length"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.from_price.term) : '';
            comparisonTable.querySelector(`[data-cell-content="initial-profile"] [data-car=${$spot}] span`).innerText = (data.car.from_price.initial_profile != null) ? data.car.from_price.initial_profile + ' months' : 'N/A';
            (parseInt(data.car.from_price.initial_profile) >= 0) ? comparisonTable.querySelector(`[data-cell-content="initial-profile"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.from_price.initial_profile) : '';
            comparisonTable.querySelector(`[data-cell-content="annual-mileage"] [data-car=${$spot}] span`).innerText = (data.car.from_price.mileage != null) ? data.car.from_price.mileage + ' miles' : 'N/A';
            (parseInt(data.car.from_price.mileage) >= 0) ? comparisonTable.querySelector(`[data-cell-content="annual-mileage"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.from_price.mileage) : '';
          }

          comparisonTable.querySelector(`[data-cell-content="vehicle-class"] [data-car=${$spot}] span`).innerText = (data.car.body_type) ? data.car.body_type : 'N/A';
          comparisonTable.querySelector(`[data-cell-content="practicality"] [data-car=${$spot}] span`).innerText = (getCapTechnicalData('Weight and Capacities', 'No. of Seats') && getCapTechnicalData('Weight and Capacities', 'No. of Seats').value) ? getCapTechnicalData('Weight and Capacities', 'No. of Seats').value + ' seats' : 'N/A';
          comparisonTable.querySelector(`[data-cell-content="engine-size"] [data-car=${$spot}] .engine-size-cc`).innerText = (getCapTechnicalData('Engine and Drive Train', 'CC') && parseInt(getCapTechnicalData('Engine and Drive Train', 'CC').value) > 1) ? getCapTechnicalData('Engine and Drive Train', 'CC').value + ' cc' : 'N/A';
          (parseInt(getCapTechnicalData('Engine and Drive Train', 'CC').value) > 1) ? comparisonTable.querySelector(`[data-cell-content="engine-size"] [data-car=${$spot}]`).setAttribute('data-compare', getCapTechnicalData('Engine and Drive Train', 'CC').value) : '';
          compareColumns('engine-size', false, true);
          comparisonTable.querySelector(`[data-cell-content="engine-size"] [data-car=${$spot}] .engine-size-litres`).innerText = (getCapTechnicalData('General', 'Badge Engine CC').value) ? ' (' + getCapTechnicalData('General', 'Badge Engine CC').value + ' Litre)' : '';
          comparisonTable.querySelector(`[data-cell-content="transmission"] [data-car=${$spot}] span`).innerText = (data.car.transmission) ? data.car.transmission : 'N/A';
          comparisonTable.querySelector(`[data-cell-content="engine-power"] [data-car=${$spot}] span`).innerText = (getCapTechnicalData('Performance', 'Engine Power - BHP') && getCapTechnicalData('Performance', 'Engine Power - BHP').value) ? getCapTechnicalData('Performance', 'Engine Power - BHP').value + ' BHP' : 'N/A';
          (parseInt(getCapTechnicalData('Performance', 'Engine Power - BHP').value) > 1) ? comparisonTable.querySelector(`[data-cell-content="engine-power"] [data-car=${$spot}]`).setAttribute('data-compare', getCapTechnicalData('Performance', 'Engine Power - BHP').value) : '';
          compareColumns('engine-power', false, true);
          comparisonTable.querySelector(`[data-cell-content="acceleration"] [data-car=${$spot}] span`).innerText = (data.car.zero_to_60) ? data.car.zero_to_60 + ' Seconds' : 'N/A';
          (parseInt(data.car.zero_to_60) > 1) ? comparisonTable.querySelector(`[data-cell-content="acceleration"] [data-car=${$spot}]`).setAttribute('data-compare', data.car.zero_to_60) : '';
          compareColumns('acceleration', false, true);
          comparisonTable.querySelector(`[data-cell-content="top-speed"] [data-car=${$spot}] span`).innerText = (getCapTechnicalData('Performance', 'Top Speed') && getCapTechnicalData('Performance', 'Top Speed').value) ? getCapTechnicalData('Performance', 'Top Speed').value + ' mph' : 'N/A';
          (getCapTechnicalData('Performance', 'Top Speed')) ? comparisonTable.querySelector(`[data-cell-content="top-speed"] [data-car=${$spot}]`).setAttribute('data-compare', getCapTechnicalData('Performance', 'Top Speed').value) : '';
          compareColumns('top-speed', false, true);
          if(getCapTechnicalData('General', 'NCAP Overall Rating') && getCapTechnicalData('General', 'NCAP Overall Rating') && getCapTechnicalData('General', 'NCAP Overall Rating').value !== 'Not Available'){
            comparisonTable.querySelector(`[data-cell-content="safety-rating"] [data-car=${$spot}] span`).innerText = '';
            for (let step = 0; step < parseInt(getCapTechnicalData('General', 'NCAP Overall Rating').value); step++) {
              let star = document.createElement("img");
              star.setAttribute('src', $base_path+'/themes/bounce/img/icons/general/star-gold.svg');
              comparisonTable.querySelector(`[data-cell-content="safety-rating"] [data-car=${$spot}] span`).appendChild(star);
            }
          } else {
            comparisonTable.querySelector(`[data-cell-content="safety-rating"] [data-car=${$spot}] span`).innerText = 'N/A';
          }
          comparisonTable.querySelector(`[data-cell-content="insurance-group"] [data-car=${$spot}] span`).innerText = (getCapTechnicalData('General', 'Insurance Group') && getCapTechnicalData('General', 'Insurance Group').value) ? getCapTechnicalData('General', 'Insurance Group').value : 'N/A';

          (data.car.active == '1' && data.car.fuel_type == 'Electric') ? comparisonTable.querySelector(`[data-cell-content="cta"] [data-car=${$spot}] a`).setAttribute('href', (typeof $base_path !== 'undefined' ? $base_path : '') + data.car.full_url) : comparisonTable.querySelector(`[data-cell-content="cta"] [data-car=${$spot}] a`).classList.add('visuallyhidden');

          ($spot === 'third') ? comparisonTable.setAttribute('data-car-form', '0') : comparisonTable.setAttribute('data-car-form', '1');

          // loadModelsCalc('car', 0, true, false)
          loadModelsCalc('car', 0, false, true)

          loadPrice($spot, $derivative)

          comparisonTable.reset();
        }).catch(function(err) {
          console.error('Fetch Error -', err);
        });
      }
    }

    function updateParams() {
      let params = '?' + new URLSearchParams({
        'term': document.querySelector('.quote-terms[name=term]').options[document.querySelector('.quote-terms[name=term]').selectedIndex].value,
        'profile': document.querySelector('.quote-terms[name=profile]').options[document.querySelector('.quote-terms[name=profile]').selectedIndex].value,
        'mileage': document.querySelector('.quote-terms[name=mileage]').options[document.querySelector('.quote-terms[name=mileage]').selectedIndex].value
      });
      comparisonTable.querySelectorAll(`[data-cell-content="cta_top"] a, [data-cell-content="model"] a, [data-cell-content="cta"] a`).forEach((value) => {
        let url;
        (value.getAttribute('href').split('?')[0].includes('/car-leasing/')) ? url = value.getAttribute('href').split('?')[0] + params : url = value.getAttribute('href').split('?')[0];
        value.setAttribute('href', url);
      })
    }


    document.querySelectorAll('.quote-terms').forEach((value) => {
      value.addEventListener('change', (ev) => {
        loadPrice('first', document.querySelector('#car-comparison').dataset.carFirst);
        loadPrice('second', document.querySelector('#car-comparison').dataset.carSecond);
        loadPrice('third', document.querySelector('#car-comparison').dataset.carThird);
        updateParams();
        ev.target.classList.add('visuallyhidden');
        ev.composedPath()[1].querySelector('button').classList.remove('visuallyhidden');
      })
    })

    document.querySelectorAll('#toggle-term, #toggle-profile, #toggle-mileage').forEach((value) => {
      value.addEventListener('click', (ev) => {
        ev.target.classList.add('visuallyhidden');
        ev.composedPath()[1].querySelector('select').classList.remove('visuallyhidden');
      })
    })

    makeField.addEventListener('change', () => {
      (makeField.value > 0) ? loadModelsCalc('car', makeField.value, false, false) : false;
    })

    modelField.addEventListener('change', () => {
      (modelField.value > 0) ? loadDerivativeCalc('car', modelField.value, true, false, true, false) : false;
      derivativeField.dataset.range = makeField.options[makeField.selectedIndex].text + ' ' + modelField.options[modelField.selectedIndex].text;
    });

    setTimeout(() => {
      (comparisonTable.dataset.carFirst > 1) ? loadVehicle('first', comparisonTable.dataset.carFirst) : null;
    }, 200);
    setTimeout(() => {
      (comparisonTable.dataset.carSecond > 1) ? loadVehicle('second', comparisonTable.dataset.carSecond) : null;
    }, 400);
    setTimeout(() => {
      if (comparisonTable.dataset.carThird > 1) {
        loadVehicle('third', comparisonTable.dataset.carThird);
        comparisonTable.setAttribute('data-car-form', '0');
      }
    }, 1000);

    derivativeField.addEventListener('change', (event) => {
      if(event.target.value > 0) {

        if(parseInt(comparisonTable.dataset.carFirst) < 1) {
          loadVehicle('first', event.target.value)
        } else if (parseInt(comparisonTable.dataset.carSecond) < 1) {
          loadVehicle('second', event.target.value)
        } else if (parseInt(comparisonTable.dataset.carThird) < 1) {
          loadVehicle('third', event.target.value)
        } else { }

        let cookie = Cookies.get('compare_list')
        let range_name = derivativeField.dataset.range
        if (cookie) {
          let cookie_obj = JSON.parse(cookie);
          if (Object.keys(cookie_obj).length < 3) {
            if (cookie_obj[event.target.value] === undefined) {
              cookie_obj = {...cookie_obj, [event.target.value] : range_name};
              let cookie_string = JSON.stringify(cookie_obj)
              Cookies.set('compare_list', cookie_string)
            }
          }
        } else {
          let cookie_obj = JSON.parse(`{"${event.target.value}":"${range_name}"}`);
          let cookie_string = JSON.stringify(cookie_obj)
          Cookies.set('compare_list', cookie_string)
        }

      }
    })

    function resetSlot($slot, $id) {
      comparisonTable.setAttribute('data-car-form', '1');
      document.querySelectorAll('div[data-car]').forEach((v) => { v.setAttribute('data-highlight', '') })
      document.querySelectorAll('div[data-car]').forEach((v) => { v.setAttribute('data-compare', '') })
      if ($slot === 'first') {
        comparisonTable.setAttribute('data-car-first', '-1');
      } else if ($slot === 'second') {
        comparisonTable.setAttribute('data-car-second', '-1');
      } else {
        comparisonTable.setAttribute('data-car-third', '-1');
      }

      let selectedVehicles = [parseInt(comparisonTable.dataset.carFirst), parseInt(comparisonTable.dataset.carSecond), parseInt(comparisonTable.dataset.carThird)];
      [...selectedVehicles].filter(val => val !== -1).forEach((value, index, array) => {
        if (index === 0) {
          (array[0]) ? loadVehicle('first', array[0]) : comparisonTable.setAttribute('data-car-first', '0');
          (array[1]) ? loadVehicle('second', array[1]) : comparisonTable.setAttribute('data-car-second', '0');
          (array[2]) ? loadVehicle('third', array[2]) : comparisonTable.setAttribute('data-car-third', '0');
          let cookie = Cookies.get('compare_list');
          let cookie_obj = JSON.parse(cookie);
          delete cookie_obj[$id];
          (Object.keys(cookie_obj).length !== 0) ? Cookies.set('compare_list', JSON.stringify(cookie_obj)) : Cookies.remove('compare_list');
        }
      })

    }
    document.querySelectorAll('.car-comparison-table-row--img>div[data-car] .reset').forEach(value => {
      value.addEventListener('click', evt => {
        comparisonTable.querySelectorAll('a.view-range-button').forEach((v) => { v.classList.remove('visuallyhidden') });
        (evt.target.classList.contains('first')) ? resetSlot('first', comparisonTable.dataset.carFirst) : null;
        (evt.target.classList.contains('second')) ? resetSlot('second', comparisonTable.dataset.carSecond) : null;
        (evt.target.classList.contains('third')) ? resetSlot('third', comparisonTable.dataset.carThird) : null;
      })
    })

  }
  // Add To Compare
  document.querySelectorAll('button[data-compare]').forEach((value) => {
    value.addEventListener('click', (e) => {
      e.preventDefault();
      let cookie = Cookies.get('compare_list')
      if (cookie) {
        let cookie_obj = JSON.parse(cookie);
        if (cookie_obj[e.target.dataset.compare] === undefined) {
          if (Object.keys(cookie_obj).length < 3) {
            cookie_obj = {...cookie_obj, [e.target.dataset.compare] : e.target.dataset.name};
            let cookie_string = JSON.stringify(cookie_obj)
            Cookies.set('compare_list', cookie_string)
            if (e.target.dataset.button === 'view') {
              e.target.innerHTML = `View`;
              const anchor = document.createElement('a');
              anchor.innerHTML = e.target.innerHTML;
              anchor.setAttribute('href', `${$base_path}/tools-calculators/electric-car-comparison-tool`);
              anchor.className = e.target.className;
              for (const attr of e.target.attributes) {
                if (attr.name.startsWith('data-')) {
                  anchor.setAttribute(attr.name, attr.value);
                }
              }
              e.target.parentNode.replaceChild(anchor, e.target);
            } else {
              e.target.innerHTML = `<svg width="9.8" height="9.8"><use xlink:href="${$base_path}/themes/bounce/img/minus.svg#minus"></use></svg>&nbsp;Remove`;
            }
            e.target.dataset.compareAdded = 'true';
            alertify.confirm('Car Comparison')
              .setting({
                'title': 'Car Comparison',
                'message': `
                  <span class="semi-bold">${e.target.dataset.name}</span> added to comparison
                  ${(cookie_obj[Object.keys(cookie_obj)[0]] !== undefined) ? `
                    <br><br> <span class="semi-bold">You are comparing:</span>
                    <ul>
                      <li><span>${cookie_obj[Object.keys(cookie_obj)[0]]}</span><span class="reset" data-compare="${Object.keys(cookie_obj)[0]}" onclick="let cookie_obj = JSON.parse(Cookies.get('compare_list')); delete cookie_obj[this.dataset.compare]; (Object.keys(cookie_obj).length !== 0) ? Cookies.set('compare_list', JSON.stringify(cookie_obj)) : Cookies.remove('compare_list'); this.parentElement.remove(); document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').dataset.compareAdded = 'false'; document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').innerHTML = '<svg width=\\x2211.8\\x22 height=\\x2211.8\\x22><use xlink:href=\\x22${$base_path}/themes/bounce/img/plus.svg#plus\\x22></use></svg>&nbsp;Compare';"></span></li>
                      ${(cookie_obj[Object.keys(cookie_obj)[1]] !== undefined) ? `<li><span>${cookie_obj[Object.keys(cookie_obj)[1]]}</span><span class="reset" data-compare="${Object.keys(cookie_obj)[1]}" onclick="let cookie_obj = JSON.parse(Cookies.get('compare_list')); delete cookie_obj[this.dataset.compare]; (Object.keys(cookie_obj).length !== 0) ? Cookies.set('compare_list', JSON.stringify(cookie_obj)) : Cookies.remove('compare_list'); this.parentElement.remove(); document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').dataset.compareAdded = 'false'; document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').innerHTML = '<svg width=\\x2211.8\\x22 height=\\x2211.8\\x22><use xlink:href=\\x22${$base_path}/themes/bounce/img/plus.svg#plus\\x22></use></svg>&nbsp;Compare';"></span></li>` : ''}
                      ${(cookie_obj[Object.keys(cookie_obj)[2]] !== undefined) ? `<li><span>${cookie_obj[Object.keys(cookie_obj)[2]]}</span><span class="reset" data-compare="${Object.keys(cookie_obj)[2]}" onclick="let cookie_obj = JSON.parse(Cookies.get('compare_list')); delete cookie_obj[this.dataset.compare]; (Object.keys(cookie_obj).length !== 0) ? Cookies.set('compare_list', JSON.stringify(cookie_obj)) : Cookies.remove('compare_list'); this.parentElement.remove(); document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').dataset.compareAdded = 'false'; document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').innerHTML = '<svg width=\\x2211.8\\x22 height=\\x2211.8\\x22><use xlink:href=\\x22${$base_path}/themes/bounce/img/plus.svg#plus\\x22></use></svg>&nbsp;Compare';"></span></li>` : ''}
                    </ul>
                  ` : ''}
                `,
                'labels': { ok: 'Go To Compare', cancel: 'Close' },
                'onok': function(){ window.location.href = $base_path+"/tools-calculators/car-comparison-tool" }
              });
          } else {
            alertify.confirm('Car Comparison')
              .setting({
                'title': 'Car Comparison',
                'message': `
                  <span class="semi-bold">Car comparison full.</span>
                  <br><br>
                  <span>If you wish to continue remove one of the items below and add ${e.target.dataset.name} again.</span>
                  <br><br> <span class="semi-bold">You are comparing:</span>
                  <ul>
                    <li><span>${cookie_obj[Object.keys(cookie_obj)[0]]}</span><span class="reset" data-compare="${Object.keys(cookie_obj)[0]}" onclick="let cookie_obj = JSON.parse(Cookies.get('compare_list')); delete cookie_obj[this.dataset.compare]; (Object.keys(cookie_obj).length !== 0) ? Cookies.set('compare_list', JSON.stringify(cookie_obj)) : Cookies.remove('compare_list'); this.parentElement.remove(); document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').dataset.compareAdded = 'false'; document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').innerHTML = '<svg width=\\x2211.8\\x22 height=\\x2211.8\\x22><use xlink:href=\\x22${$base_path}/themes/bounce/img/plus.svg#plus\\x22></use></svg>&nbsp;Compare';"></span></li>
                    <li><span>${cookie_obj[Object.keys(cookie_obj)[1]]}</span><span class="reset" data-compare="${Object.keys(cookie_obj)[1]}" onclick="let cookie_obj = JSON.parse(Cookies.get('compare_list')); delete cookie_obj[this.dataset.compare]; (Object.keys(cookie_obj).length !== 0) ? Cookies.set('compare_list', JSON.stringify(cookie_obj)) : Cookies.remove('compare_list'); this.parentElement.remove(); document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').dataset.compareAdded = 'false'; document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').innerHTML = '<svg width=\\x2211.8\\x22 height=\\x2211.8\\x22><use xlink:href=\\x22${$base_path}/themes/bounce/img/plus.svg#plus\\x22></use></svg>&nbsp;Compare';"></span></li>
                    <li><span>${cookie_obj[Object.keys(cookie_obj)[2]]}</span><span class="reset" data-compare="${Object.keys(cookie_obj)[2]}" onclick="let cookie_obj = JSON.parse(Cookies.get('compare_list')); delete cookie_obj[this.dataset.compare]; (Object.keys(cookie_obj).length !== 0) ? Cookies.set('compare_list', JSON.stringify(cookie_obj)) : Cookies.remove('compare_list'); this.parentElement.remove(); document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').dataset.compareAdded = 'false'; document.querySelector('button[data-compare=\\x22'+this.dataset.compare+'\\x22]').innerHTML = '<svg width=\\x2211.8\\x22 height=\\x2211.8\\x22><use xlink:href=\\x22${$base_path}/themes/bounce/img/plus.svg#plus\\x22></use></svg>&nbsp;Compare';"></span></li>
                  </ul>
                `,
                'labels': { ok: 'Go To Compare', cancel: 'Close' },
                'onok': function(){ window.location.href = $base_path+"/tools-calculators/car-comparison-tool" }
              });
          }
        } else {
          delete cookie_obj[e.target.dataset.compare];
          (Object.keys(cookie_obj).length !== 0) ? Cookies.set('compare_list', JSON.stringify(cookie_obj)) : Cookies.remove('compare_list');
          e.target.innerHTML = `<svg width="11.8" height="11.8"><use xlink:href="${$base_path}/themes/bounce/img/plus.svg#plus"></use></svg>&nbsp;Compare`;
          e.target.dataset.compareAdded = 'false';
          alertify.confirm('Car Comparison')
            .setting({
              'title': 'Car Comparison',
              'message': `<span class="semi-bold">${e.target.dataset.name}</span> removed from comparison`,
              'labels': { ok: 'Go To Compare', cancel: 'Close' },
              'onok': function(){ window.location.href = $base_path+"/tools-calculators/car-comparison-tool" }
            });
        }
      } else {
        let cookie_obj = JSON.parse(`{"${e.target.dataset.compare}":"${e.target.dataset.name}"}`);
        let cookie_string = JSON.stringify(cookie_obj)
        Cookies.set('compare_list', cookie_string)
        if (e.target.dataset.button === 'view') {
          e.target.innerHTML = `View`;
          const anchor = document.createElement('a');
          anchor.innerHTML = e.target.innerHTML;
          anchor.setAttribute('href', `${$base_path}/tools-calculators/electric-car-comparison-tool`);
          anchor.className = e.target.className;
          for (const attr of e.target.attributes) {
            if (attr.name.startsWith('data-')) {
              anchor.setAttribute(attr.name, attr.value);
            }
          }
          e.target.parentNode.replaceChild(anchor, e.target);
        } else {
          e.target.innerHTML = `<svg width="9.8" height="9.8"><use xlink:href="${$base_path}/themes/bounce/img/minus.svg#minus"></use></svg>&nbsp;Remove`;
        }
        e.target.dataset.compareAdded = 'true';

        alertify.confirm('Car Comparison')
          .setting({
            'title': 'Car Comparison',
            'message': `<span class="semi-bold">${e.target.dataset.name}</span> added to comparison`,
            'labels': { ok: 'Go To Compare', cancel: 'Close' },
            'onok': function(){ window.location.href = $base_path+"/tools-calculators/car-comparison-tool" }
          });
      }
    })
  })

  // Gauges
  if (document.querySelector('#charging-speeds')) {
    const gaugeOpts = {
      angle: -0.25, lineWidth: 0.10, radiusScale: 1, strokeColor: '#F2F3F3',
      generateGradient: true, highDpiSupport: true,
      pointer: { length: 0, strokeWidth: 0 }
    }
    const gaugeHomeOpts = { colorStart: '#76BC43', colorStop: '#76BC43'};
    const gaugeFastOpts = { colorStart: '#FCB623', colorStop: '#FCB623' };
    const gaugeRapidOpts = { colorStart: '#fc8423', colorStop: '#fc8423' };
    const gaugeUltraOpts = { colorStart: '#FC4F23', colorStop: '#FC4F23' };

    function runGauge($target, $opts, $animationSpeed, $maxValue) {
      if ($target){
        let gauge = new Gauge($target).setOptions({...gaugeOpts, ...$opts});
        gauge.maxValue = $maxValue;
        gauge.setMinValue(0);
        gauge.animationSpeed = $animationSpeed;
        gauge.set(($maxValue - $target.dataset.minutes));
      }
    }

    let chargingSpeedsSection = document.getElementById('charging-speeds').getBoundingClientRect();
    let offsetToTriggerAnimation = chargingSpeedsSection.y + (chargingSpeedsSection.height/4);
    function scrollAnimTriggerCheck(e) {
      let viewBottom = window.scrollY + window.innerHeight;
      if (viewBottom > offsetToTriggerAnimation) {
        runGauge(document.getElementById('gaugeHome'), gaugeHomeOpts, 500, 1440);
        runGauge(document.getElementById('gaugeFast'), gaugeFastOpts, 400, 1440);
        runGauge(document.getElementById('gaugeRapid'), gaugeRapidOpts, 300, 1440);
        runGauge(document.getElementById('gaugeUltra'), gaugeUltraOpts, 200, 1440);
        document.removeEventListener("scroll", scrollAnimTriggerCheck);
      }
    }
    document.addEventListener("scroll", scrollAnimTriggerCheck);
  }

  // Deal Finder Strip
  if (document.querySelector('#deal-finder-strip')) {

    let typeSwitch = document.querySelectorAll('#deal-finder-category input');
    // let chargerSwitch = document.querySelectorAll('#deal-finder-charger input');
    let typeSections = document.querySelectorAll('.deal-finder-container > section');

    let makeField = document.querySelector('select[name=deal-finder-make]');
    let modelField = document.querySelector('select[name=deal-finder-model]');
    let carUrl = $base_path+'/electric-leasing';
    let carText = 'Browse EV Models';
    let carSearchButton = document.querySelector('#search-car-button');
    let carSearchUrl = new URL(window.location.origin + $base_path + '/search');
    carSearchUrl.searchParams.append("search-type", "Car");

    let vanMakeField = document.querySelector('select[name=van-deal-finder-make]');
    let vanModelField = document.querySelector('select[name=van-deal-finder-model]');
    let vanUrl = '/electric-van-leasing';
    let vanText = 'Browse Electric Vans';
    let vanSearchButton = document.querySelector('#search-van-button');
    let vanSearchUrl = new URL(window.location.origin + $base_path + '/search');
    vanSearchUrl.searchParams.append("search-type", "Van");

    setTimeout(() => { document.getElementById("deal-finder-form").reset() }, 500);

    function loadManufacturersFinder($type) {
      var make_dropdowns;
      ($type == 'car') ? make_dropdowns = document.querySelector('select[name=deal-finder-make]') : make_dropdowns = document.querySelector('select[name=van-deal-finder-make]');
      for (i = 0; i < make_dropdowns.length; i++) {

        var dropdown = make_dropdowns;
        dropdown.length = 0;

        var defaultOption = document.createElement('option');
        defaultOption.text = 'Any Make';
        defaultOption.value = '';

        dropdown.add(defaultOption);
        dropdown.selectedIndex = 0;

        var url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/'+$type+'/manufacturers';

        fetch(url).then(function(response) {
          if (response.status !== 200) {
            console.warn('Looks like there was a problem. Status Code: ' +
              response.status);
            return;
          }
          return response.json();
        }).then(function(data) {
          let keysSorted = Object.keys(data).sort(function(a, b) {
            return data[a].localeCompare(data[b])
          });
          for (let x in keysSorted) {
            let option = document.createElement('option');
            option.text = data[keysSorted[x]];
            option.value = keysSorted[x];
            dropdown.add(option);
          }
        }).catch(function(err) {
          console.error('Fetch Error -', err);
        });
      }
    }

    typeSwitch.forEach((value) => {
      value.addEventListener('change', (e) => {
        typeSections.forEach((v) => {
          (v.dataset.type === e.target.value) ? v.classList.add('active') : v.classList.remove('active')
        })
      })
    })

    // Charger Switches
    // chargerSwitch.forEach((value) => {
    //   value.addEventListener('change', (e) => {
    //     (e.target.value === 'yes') ? document.querySelector('#charging-button').setAttribute('href', '-charging/home-charging') : document.querySelector('#charging-button').setAttribute('href', '-charging/workplace-charging');
    //     (e.target.value === 'yes') ? document.querySelector('#charging-button').innerText = 'Home Charging' : document.querySelector('#charging-button').innerText = 'Workplace Charging';
    //   })
    // })

    // Car Switches
    document.querySelectorAll('#q1-switch input').forEach((value => {
      value.addEventListener('change', e => {
        loadManufacturersFinder('car');
        document.querySelectorAll('#car-question-two > div').forEach(v => {
          (v.dataset.type === e.target.value) ? v.classList.add('active') : v.classList.remove('active')
          document.querySelector('#q1-switch').parentElement.classList.add('visuallyhidden')
        })
      })
    }))
    document.querySelectorAll('#bodytype-switch input').forEach((value) => {
      value.addEventListener('change', (e) => {
        document.querySelector('.deal-finder-car .question-three-form').classList.add('active');
        document.querySelector('#bodytype-switch').parentElement.classList.add('visuallyhidden');
        if (e.target.value === '4x4') {
          carSearchUrl.searchParams.delete("body_type");
          carSearchUrl.searchParams.set("body_type[]", e.target.value);
          carSearchUrl.searchParams.append("body_type[]", 'crossover');
          carSearchUrl.searchParams.append("body_type[]", 'estate');
          carSearchUrl.searchParams.append("body_type[]", 'suv');
        } else if (e.target.value === 'any') {
          carSearchUrl.searchParams.delete("body_type[]");
          carSearchUrl.searchParams.set("body_type", e.target.value);
        } else {
          carSearchUrl.searchParams.delete("body_type");
          carSearchUrl.searchParams.set("body_type[]", e.target.value);
        }
        carSearchButton.querySelector('a').setAttribute('href', carSearchUrl.href)
      })
    })
    // document.querySelectorAll('#budget-switch input').forEach((value) => {
    //   value.addEventListener('change', (e) => {
    //     document.querySelector('.deal-finder-car .question-four-form').classList.add('active');
    //     carSearchUrl.searchParams.set("budget", e.target.value);
    //     carSearchButton.querySelector('a').setAttribute('href', carSearchUrl.href)
    //   })
    // })
    document.querySelectorAll('#range-switch input').forEach((value) => {
      value.addEventListener('change', (e) => {
        document.querySelector('.deal-finder-car .question-four-form').classList.add('active');
        document.querySelector('#range-switch').parentElement.classList.add('visuallyhidden');
        carSearchUrl.searchParams.set("electric_range", e.target.value);
        carSearchButton.querySelector('a').setAttribute('href', carSearchUrl.href)
      })
    })
    document.querySelectorAll('#type-switch input').forEach((value) => {
      value.addEventListener('change', (e) => {
        carSearchUrl.searchParams.set("type", e.target.value);
        carSearchButton.classList.add('active');
        carSearchButton.querySelector('a').setAttribute('href', carSearchUrl.href)
      })
    })

    // Van Switches
    document.querySelectorAll('#van-q1-switch input').forEach((value => {
      value.addEventListener('change', e => {
        loadManufacturersFinder('van');
        document.querySelectorAll('#van-question-two > div').forEach(v => {
          (v.dataset.type === e.target.value) ? v.classList.add('active') : v.classList.remove('active')
          document.querySelector('#van-q1-switch').parentElement.classList.add('visuallyhidden');
        })
      })
    }))
    document.querySelectorAll('#van-bodytype-switch input').forEach((value) => {
      value.addEventListener('change', (e) => {
        document.querySelector('.deal-finder-van .van-question-three-form').classList.add('active');
        document.querySelector('#van-bodytype-switch').parentElement.classList.add('visuallyhidden');
        vanSearchUrl.searchParams.set("van_size", e.target.value);
        vanSearchButton.querySelector('a').setAttribute('href', vanSearchUrl.href)
      })

    })
    // document.querySelectorAll('#van-budget-switch input').forEach((value) => {
    //   value.addEventListener('change', (e) => {
    //     document.querySelector('.deal-finder-van .van-question-four-form').classList.add('active');
    //     vanSearchUrl.searchParams.set("budget", e.target.value);
    //     vanSearchButton.querySelector('a').setAttribute('href', vanSearchUrl.href)
    //   })
    // })
    document.querySelectorAll('#van-range-switch input').forEach((value) => {
      value.addEventListener('change', (e) => {
        document.querySelector('.deal-finder-van .van-question-four-form').classList.add('active');
        document.querySelector('#van-range-switch').parentElement.classList.add('visuallyhidden');
        vanSearchUrl.searchParams.set("electric_range", e.target.value);
        vanSearchButton.querySelector('a').setAttribute('href', vanSearchUrl.href)
      })
    })
    document.querySelectorAll('#van-type-switch input').forEach((value) => {
      value.addEventListener('change', (e) => {
        vanSearchUrl.searchParams.set("type", e.target.value);
        vanSearchButton.classList.add('active');
        vanSearchButton.querySelector('a').setAttribute('href', vanSearchUrl.href)
      })
    })

    // Reset Button
    document.querySelector('.deal-finder-reset button').addEventListener('click', (e) => {
      document.querySelectorAll('#q1-switch, #bodytype-switch, #range-switch, #van-q1-switch, #van-bodytype-switch, #van-range-switch').forEach(value => {
        value.parentElement.classList.remove('visuallyhidden');
      })
      typeSections.forEach(value => {
        value.classList.remove('active');
      })
      document.querySelectorAll('#search-van-button, #search-car-button, .question-two > div, .question-three > div, .question-four > div, .van-question-two > div, .van-question-three > div, .van-question-four > div').forEach(value => {
        value.classList.remove('active');
      })
      document.querySelector('.deal-finder-strip-nav').classList.add('active');
      document.getElementById('q2-button').innerHTML = 'Browse EV Models';
      document.getElementById('van-q2-button').innerHTML = 'Browse Electric Vans';
      document.getElementById('q2-button').setAttribute('href', $base_path+'/cars');
      document.getElementById('van-q2-button').setAttribute('href', $base_path+'/vans');
    })

    // Load Models Dropdown
    function loadModelsCharger($type, $manufacturer) {
      let model_dropdowns;
      ($type == 'car') ? model_dropdowns = document.getElementsByName("deal-finder-model") : model_dropdowns = document.getElementsByName("van-deal-finder-model");
      for (let i = 0; i < model_dropdowns.length; i++) {
        let dropdown = model_dropdowns[i];
        dropdown.length = 0;

        let defaultOption = document.createElement('option');
        defaultOption.text = 'Select Model';
        defaultOption.value = '';

        dropdown.add(defaultOption);
        dropdown.selectedIndex = 0;

        let url = (typeof $base_path !== 'undefined' ? $base_path : '')+'/api/' + $type + '/models/' + $manufacturer + '';

        fetch(url).then(function(response) {
          if (response.status !== 200) {
            console.warn('Looks like there was a problem. Status Code: ' +
              response.status);
            return;
          }
          return response.json();
        }).then(function(data) {
          let keysSorted = Object.keys(data).sort(function(a, b) {
            return data[a].localeCompare(data[b])
          });
          for (let x in keysSorted) {
            let option = document.createElement('option');
            option.text = data[keysSorted[x]];
            option.value = keysSorted[x];
            dropdown.add(option);
          }
        }).catch(function(err) {
          console.error('Fetch Error -', err);
        });
      }
    }

    // Car Dropdowns
    makeField.addEventListener('change', () => {
      (makeField.value > 0) ? loadModelsCharger('car', makeField.value) : loadModelsCharger('car', 0);

      (makeField.value > 0) ? carUrl = $base_path+'/cars/' + makeField.options[makeField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase() : carUrl = $base_path+'/electric-leasing';
      document.getElementById('q2-button').setAttribute('href', carUrl);

      (makeField.value > 0) ? carText = 'Browse ' + makeField.options[makeField.selectedIndex].text + ' Models' : carText = 'Browse EV Models';
      document.getElementById('q2-button').innerText = carText;
    })
    modelField.addEventListener('change', () => {
      (modelField.value > 0) ?
        carUrl = $base_path+'/cars/' + makeField.options[makeField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase() + '/' + modelField.options[modelField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase() :
        carUrl = ((makeField.value > 0) ? carUrl = $base_path+'/cars/' + makeField.options[makeField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase() : carUrl = $base_path+'/electric-leasing');
      document.getElementById('q2-button').setAttribute('href', carUrl);

      (modelField.value > 0) ?
        carText = 'Browse ' + makeField.options[makeField.selectedIndex].text + ' ' + modelField.options[modelField.selectedIndex].text + ' Models' :
        carText = ((makeField.value > 0) ? carText = 'Browse ' + makeField.options[makeField.selectedIndex].text + ' Models' : carText = 'Browse EV Models');
      document.getElementById('q2-button').innerText = carText;
    })

    // Van Dropdowns
    vanMakeField.addEventListener('change', () => {
      (vanMakeField.value > 0) ? loadModelsCharger('van', vanMakeField.value) : loadModelsCharger('van', 0);

      (vanMakeField.value > 0) ? vanUrl = $base_path+'/vans/' + vanMakeField.options[vanMakeField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase() : vanUrl = $base_path+'/electric-van-leasing';
      document.getElementById('van-q2-button').setAttribute('href', vanUrl);

      (vanMakeField.value > 0) ? vanText = 'Browse ' + vanMakeField.options[vanMakeField.selectedIndex].text + ' Models' : vanText = 'Browse Electric Vans';
      document.getElementById('van-q2-button').innerText = vanText;
    })
    vanModelField.addEventListener('change', () => {
      (vanModelField.value > 0) ?
        vanUrl = $base_path+'/vans/' + vanMakeField.options[vanMakeField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase() + '/' + vanModelField.options[vanModelField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase() :
        vanUrl = ((vanMakeField.value > 0) ? vanUrl = $base_path+'/vans/' + vanMakeField.options[vanMakeField.selectedIndex].text.replace(/\s+/g, '-').toLowerCase() : vanUrl = $base_path+'/electric-van-leasing');
      document.getElementById('van-q2-button').setAttribute('href', vanUrl);

      (vanModelField.value > 0) ?
        vanText = 'Browse ' + vanMakeField.options[vanMakeField.selectedIndex].text + ' ' + vanModelField.options[vanModelField.selectedIndex].text + ' Models' :
        vanText = ((vanMakeField.value > 0) ? vanText = 'Browse ' + vanMakeField.options[vanMakeField.selectedIndex].text + ' Models' : vanText = 'Browse Electric Vans');
      document.getElementById('van-q2-button').innerText = vanText;
    })
  }

  // Show More
  const showMoreButton = document.querySelector('#show-more');
  const headerContent = document.querySelector('#manufacturer-header-content');
  const headerContentSearch = document.querySelector('.search-header-content > .show-more-text');
  if(showMoreButton && headerContent){
    if (showMoreButton.parentElement.querySelectorAll('.header-description > *').length > 1) {
      showMoreButton.addEventListener('click', (e) => {
        headerContent.classList.toggle('header-content--full');
        showMoreButton.classList.toggle('active');
        (showMoreButton.classList.contains('active')) ? showMoreButton.querySelector('span').innerText = 'Show Less' : showMoreButton.querySelector('span').innerText = 'Read More';
      })
    } else {
      showMoreButton.classList.add('visuallyhidden')
    }
  } else if (showMoreButton && headerContentSearch) {
    showMoreButton.addEventListener('click', (e) => {
      headerContentSearch.classList.toggle('active');
      showMoreButton.classList.toggle('active');
      (showMoreButton.classList.contains('active')) ? showMoreButton.querySelector('span').innerText = 'Show Less' : showMoreButton.querySelector('span').innerText = 'Read More';
    })
  }

});


function debounce(func, wait) {
    let timeout;
    return function(...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
}